import { sanitizeSku } from "@shared/sku";
import { suggestSubjectCharacter } from "@shared/subjectCharacter";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  Archive,
  ArchiveRestore,
  Check,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  ImageOff,
  Link2,
  PackageSearch,
  Pencil,
  Plus,
  ShieldAlert,
  SlidersHorizontal,
  Store,
  Trash2,
  Unlink,
  X,
} from "lucide-react";
import { Fragment, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MarketplaceIcon } from "@/components/MarketplaceIcon";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ApiClientError, api, apiWithMeta } from "../lib/api";
import { dateOnly, integer, money } from "../lib/format";
import i18n from "../lib/i18n";
import { defaultLicenseKindForKind } from "../lib/licenseDefaults";
import { marketplaceListingUrl } from "../lib/marketplaceUrls";
import { useDebounced } from "../lib/useDebounced";
import { cn } from "../lib/utils";

type Unit = "stickers" | "figures" | "electronics" | "channels" | "platform";
export type LicenseKind = "original" | "licensed" | "public_domain" | "generic" | "unreviewed";
type Brand = "ninjamo" | "peeker";
type ProductKind =
  | "sticker_single"
  | "sticker_sheet"
  | "card_wrap"
  | "figure"
  | "electronics"
  | "bundle"
  | "other";
type MovementKind = "receipt" | "sale" | "return" | "adjustment" | "transfer" | "damage";

interface Row {
  id: string;
  sku: string;
  title: string;
  businessUnit: Unit;
  kind: string;
  status: "draft" | "active" | "discontinued" | "archived";
  franchise: string | null;
  licenseKind: LicenseKind;
  brand: Brand | null;
  landedCost: string | null;
  listPrice: string | null;
  mpn: string | null;
  barcode: string | null;
  binManagerCategoryId: number | null;
  binManagerSku: string | null;
  binManagerPushedAt: string | null;
  onHand: number;
  reserved: number;
  sellable: number;
  stockMode: "tracked" | "made_to_order";
  madeToOrderQuantity: number;
  /** Ordered image URLs, first is primary. Empty until fetched from a
   * channel that already has some (see the "Fetch images" action). */
  images: string[];
  /** Distinct marketplaces this SKU has a channel_listings row on. */
  channels: string[];
  /** Trailing 30d, all channels combined -- the full per-channel/period
   * breakdown lives behind the run-rate query below, fetched only when a row
   * is expanded. */
  unitsPerDay: number;
  /** All-time (not windowed like unitsPerDay) -- null means never sold, not
   * "no recent sales". Same order-status filter as unitsPerDay. */
  lastSoldAt: string | null;
  /** Trailing-30d fast approximation: revenue − COGS − ad spend. Null (not
   * 0) when the product has no landedCost/unitCost -- an unknown cost must
   * never silently price in as free. Excludes channel fees, shipping cost
   * and refunds -- see GET /products/:sku/profit's own per-channel figure
   * for those, fetched only when a row is expanded. */
  grossMargin30d: number | null;
  /** Null when this SKU has no Amazon channel_listings row or has never
   * been FBA-synced -- there's nothing to classify yet, not a 4th bucket. */
  fbaStockHealth: "restock" | "healthy" | "overstock" | "no_sales_data" | null;
  /** Independent of fbaStockHealth -- damaged/defective/expired units in an
   * FC are worth a flag regardless of the stock-level bucket. */
  fbaHasUnfulfillable: boolean;
  /** Raw ingredients behind fbaStockHealth -- Isaac, 2026-09-19: the bucket
   * badge alone didn't show the actual unit count driving it. Null under
   * the same condition as fbaStockHealth (never FBA-synced). */
  fbaFulfillableQuantity: number | null;
  fbaInboundQuantity: number | null;
  /** Amazon's own catalogue id (ASIN) for this SKU -- null until synced, or
   * if this product has never been listed on Amazon. */
  amazonAsin: string | null;
  /** Amazon's own restock recommendation -- a separate signal from
   * fbaStockHealth (this app's own run-rate calculation), which may agree or
   * disagree. Null when there's no still-pending flag, not a false "healthy". */
  restockFlagId: string | null;
  restockFlagSeverity: "low" | "medium" | "high" | null;
}

/** One channel's listing detail for the expanded row — GET /products/:sku's
 * `listings` array, extended with the allocation/content fields added for
 * this feature. */
interface ListingDetail {
  id: string;
  status: "draft" | "pending" | "active" | "suppressed" | "ended";
  price: string | null;
  externalId: string | null;
  channelSku: string | null;
  publishedQuantity: number | null;
  marketplaceId: string | null;
  allocationPercent: string | null;
  intendedQuantity: number | null;
  suppressionReason: string | null;
  lastSyncedAt: string | null;
  marketplace: string;
  accountLabel: string;
  hasContentOverride: boolean;
  sellable: number;
}

interface ProductDetail {
  product: Row;
  listings: ListingDetail[];
}

interface RunRateChannel {
  marketplace: string;
  accountLabel: string;
  unitsSold: number;
  revenueUsd: string;
  unitsPerDay: number;
}

interface RunRate {
  period: string;
  totalUnitsSold: number;
  totalRevenueUsd: string;
  unitsPerDay: number;
  byChannel: RunRateChannel[];
}

/** One channel's margin detail for the expanded row -- GET
 * /products/:sku/profit's `byChannel` entries. Money fields are strings
 * (server-formatted to 2dp); `null` fields mean "unknown," not zero -- see
 * that endpoint's own doc comment for why (no landedCost/unitCost set). */
interface ProfitChannel {
  marketplace: string;
  accountLabel: string;
  unitsSold: number;
  /** Of unitsSold, how many were merchant/FBM-fulfilled (ShipStation) --
   * only this portion gets shippingCost deducted; FBA's shipping is inside
   * Amazon's own fees, not tracked here. */
  unitsSoldMerchant: number;
  revenueUsd: string;
  feePercent: number | null;
  channelFee: string | null;
  landedCostTotal: string | null;
  adSpendUsd: string;
  /** Null when merchant units were sold but this SKU has no clean
   * single-item ShipStation shipment history yet -- unknown, not $0. */
  shippingCost: string | null;
  netMargin: string | null;
}

interface ProfitDetail {
  period: string;
  unitCostUsed: string | null;
  /** All-time average, not windowed to `period` -- see the endpoint's own
   * doc comment. Null if this SKU has never shipped as a clean
   * single-item FBM order. */
  avgFbmShipCostPerUnit: string | null;
  totalRevenueUsd: string;
  totalNetMargin: string | null;
  byChannel: ProfitChannel[];
}

interface ContentOverride {
  effective: {
    title: string;
    bullets: string[];
    description: string;
    fields: Record<string, unknown>;
  };
}

/** A live channel listing that hasn't been linked to an internal product yet
 * — everything real about it (title, price, stock) comes straight from the
 * channel, not our own catalogue. Shown alongside matched products so "all
 * of my listings and their stock" (Isaac, 2026-08-29) means all of it, not
 * just the fraction that's been matched so far. */
interface UnmatchedRow {
  id: string;
  channelId: string;
  marketplace: string;
  accountLabel: string;
  channelSku: string;
  marketplaceId: string | null;
  title: string | null;
  price: string | null;
  publishedQuantity: number | null;
  /** How many OTHER channels have this exact channelSku pending too — see
   * server/routes/channels.ts's crossChannelCount comment for why this is a
   * trustworthy signal, not a guess. */
  crossChannelCount: number;
}

interface CrossChannelGroup {
  channelSku: string;
  title: string | null;
  channels: Array<{
    id: string;
    channelId: string;
    marketplace: string;
    accountLabel: string;
    price: string | null;
    publishedQuantity: number | null;
  }>;
}

/** GET /api/v1/inventory/fba-inbound-shipments -- shipment-level only, by
 * request ("keep everything simple"); no SKU/quantity breakdown. */
interface InboundShipment {
  shipmentId: string;
  shipmentName: string | null;
  destinationFulfillmentCenterId: string | null;
  status: string;
}

interface InventorySummary {
  /** Distinct products active on at least one channel -- not a sum of the
   * per-marketplace counts below, which are listing counts and can overlap
   * (one SKU active on two channels counts once here, twice split below). */
  activeSkusTotal: number;
  activeByMarketplace: Array<{ marketplace: string; count: number }>;
  byChannel: Array<{ marketplace: string; accountLabel: string; count: number }>;
  fbaStockHealthCounts: {
    restock: number;
    healthy: number;
    overstock: number;
    no_sales_data: number;
  };
  /** Isaac, 2026-09-23: "see exactly what is out of stock on each platform" --
   * same shape/convention as activeByMarketplace, just the out_of_stock
   * count instead of the active count. Only present marketplaces (count > 0)
   * are included, same filtering activeByMarketplace's sibling does. */
  outOfStockByMarketplace: Array<{ marketplace: string; count: number }>;
}

const UNIT_FILTERS: Array<Unit | "all"> = ["all", "stickers", "figures", "electronics"];
/** Inventory items are only ever these three lines — `channels`/`platform` are the
 * app registry's business units, not something you stock. */
const INVENTORY_UNITS: Unit[] = ["stickers", "figures", "electronics"];
/** Bin Manager (binmanager.mitechnologiesinc.com) category IDs -- confirmed
 * live 2026-09-24 against real Ninjamo catalog data (searching existing
 * SNAS/SNFA-prefixed SKUs) and Isaac's own direct confirmation: "Anime
 * Stickers" covers ALL of Ninjamo's stickers regardless of theme, not just
 * anime ones. No live category-list endpoint exists for this app to pull
 * from, so this is a fixed lookup rather than a fetched list. "Custom" lets
 * a human enter any other numeric category id directly -- Isaac, 2026-09-24:
 * "dont restrict us to just anime stickers and figures" (Ninjamo also sells
 * electronics, which has no known default category here). */
const BINMANAGER_CATEGORY_OPTIONS: Array<{ id: string; label: string }> = [
  { id: "21", label: "Anime Stickers" },
  { id: "19", label: "Anime Figures" },
  { id: "custom", label: "Other (enter category ID)" },
];
const PRODUCT_KINDS: ProductKind[] = [
  "sticker_single",
  "sticker_sheet",
  "card_wrap",
  "figure",
  "electronics",
  "bundle",
  "other",
];
const LICENSE_KINDS: LicenseKind[] = [
  "original",
  "licensed",
  "public_domain",
  "generic",
  "unreviewed",
];

const MOVEMENT_KINDS: MovementKind[] = [
  "receipt",
  "sale",
  "return",
  "adjustment",
  "transfer",
  "damage",
];

export const LICENSE_VARIANT: Record<
  LicenseKind,
  "secondary" | "info" | "warning" | "destructive"
> = {
  original: "secondary",
  licensed: "info",
  public_domain: "secondary",
  // Confirmed unofficial/China-sourced reproduction -- a KNOWN risk, so it
  // reads more alarming than "unreviewed" (merely unassessed).
  generic: "destructive",
  unreviewed: "warning",
};

const FBA_STOCK_VARIANT: Record<
  NonNullable<Row["fbaStockHealth"]>,
  "success" | "info" | "warning" | "destructive"
> = {
  restock: "destructive",
  // Isaac, 2026-09-19: was "secondary" (neutral grey), which read as flat/
  // ambiguous next to the unfulfillable-units warning icon -- a clearly
  // positive green + checkmark disambiguates "stock level is fine" from
  // "there's also a separate damage flag on this SKU."
  healthy: "success",
  overstock: "warning",
  no_sales_data: "info",
};

/** Channels whose server-side client implements `pushListing` (see
 * server/lib/marketplaces/types.ts) -- everything else still gets the plain
 * "not pushed live" banner with no action, same as before this feature. */
const PUSHABLE_MARKETPLACES = ["shopify", "amazon"];

/** Amazon's pushListing only implements the STICKER_DECAL product type so far
 * (Isaac, 2026-09-20 -- confirmed live against this account's real schema);
 * figures/electronics use different, uninvestigated Amazon product types.
 * Checked ONLY for Amazon -- every other pushable marketplace has no such
 * per-product-type restriction yet. */
function amazonPushSupported(businessUnit: Unit): boolean {
  return businessUnit === "stickers";
}

/** Channels whose server-side client implements `deleteListing` -- everything
 * else 409s cleanly from POST /listings/:id/delete-remote if hit directly,
 * but there's no reason to show the button at all for a channel that can't
 * act on it. */
const REMOTE_DELETABLE_MARKETPLACES = ["shopify"];

/** Fixed order/set for the per-marketplace active-SKU stat boxes -- always
 * all four, even at 0, so a platform with nothing active still reads as
 * "confirmed zero" rather than silently missing (unlike CHANNEL_FILTERS
 * above, which only shows a channel once it has active listings). */
const STAT_MARKETPLACES = ["amazon", "ebay", "shopify", "walmart"];

/** Brand names, not translated -- same reasoning as the channel filter
 * logos above (MarketplaceIcon's own header comment): these are proper
 * nouns, identical in every locale this app supports. */
const MARKETPLACE_LABELS: Record<string, string> = {
  amazon: "Amazon",
  ebay: "eBay",
  shopify: "Shopify",
  walmart: "Walmart",
};

export default function Inventory() {
  const { t } = useTranslation();
  const locale = i18n.resolvedLanguage ?? "en";
  const qc = useQueryClient();
  const [unit, setUnit] = useState<Unit | "all">("all");
  const [term, setTerm] = useState("");
  // "all" = no filter, "unlisted" = the third state productQuerySchema's
  // listedOn param exists for (no channel_listings row at all), anything
  // else is a literal marketplace enum value.
  const [channelFilter, setChannelFilter] = useState<string>("all");
  // Isaac, 2026-09-18: the 5-button licence filter row (All/Original/
  // Licensed/Public domain/Unreviewed) read as too many confusing options
  // for a team he plans to eventually give access to. Only one of those
  // five ever mattered operationally -- narrowing to what still needs
  // review -- so this is now a single checkbox, not a five-way picker.
  const [unreviewedOnly, setUnreviewedOnly] = useState(false);
  // Two checkboxes, but a product only ever has one brand -- checking one
  // clears the other rather than allowing a contradictory "both" state.
  const [brandFilter, setBrandFilter] = useState<Brand | "all">("all");
  const [fbaOnly, setFbaOnly] = useState(false);
  // One specific bucket within "FBA Stock Only" (Isaac, 2026-09-19: "healthy,
  // overstock, re-stock so i can see exactly how many of each"). "all" means
  // the plain fbaOnly checkbox behavior -- every FBA-synced SKU regardless
  // of bucket.
  const [fbaBucket, setFbaBucket] = useState<
    "all" | "restock" | "healthy" | "overstock" | "no_sales_data"
  >("all");
  // One specific marketplace within "Out of stock" (Isaac, 2026-09-23: "see
  // exactly what is out of stock on each platform"). "all" means every
  // marketplace, same convention as fbaBucket's own "all" above.
  const [outOfStockMarketplace, setOutOfStockMarketplace] = useState("all");
  // FBA is Amazon-specific -- the checkbox (and bucket pills) only make
  // sense, and are only shown, on the Amazon channel tab. Reset rather than
  // just hide: leaving them checked-but-invisible after switching channels
  // would silently keep filtering with no visible control left to undo it.
  useEffect(() => {
    if (channelFilter !== "amazon") {
      if (fbaOnly) setFbaOnly(false);
      if (fbaBucket !== "all") setFbaBucket("all");
      // Brand (Ninjamo/Peeker) is an Amazon-only concept today -- doesn't
      // relate to eBay or Shopify listings (Isaac, 2026-09-20). Same
      // reset-on-switch reasoning as FBA above.
      if (brandFilter !== "all") setBrandFilter("all");
    }
    // Same reset-on-switch reasoning, for the out-of-stock marketplace pill.
    if (channelFilter !== "out_of_stock" && outOfStockMarketplace !== "all") {
      setOutOfStockMarketplace("all");
    }
  }, [channelFilter, fbaOnly, fbaBucket, brandFilter, outOfStockMarketplace]);
  // Unchecking "FBA Stock Only" should drop any bucket selection too --
  // otherwise re-checking it later would silently reapply a stale bucket
  // filter with no visible indication why.
  useEffect(() => {
    if (!fbaOnly && fbaBucket !== "all") setFbaBucket("all");
  }, [fbaOnly, fbaBucket]);

  const params = new URLSearchParams({ limit: "200" });
  if (unit !== "all") params.set("businessUnit", unit);
  if (term.trim()) params.set("q", term.trim());
  if (channelFilter === "unlisted") params.set("listedOn", "unlisted");
  else if (channelFilter === "out_of_stock") {
    params.set("listedOn", "out_of_stock");
    if (outOfStockMarketplace !== "all") params.set("marketplace", outOfStockMarketplace);
  } else if (channelFilter !== "all") params.set("marketplace", channelFilter);
  if (unreviewedOnly) params.set("licenseKind", "unreviewed");
  if (brandFilter !== "all") params.set("brand", brandFilter);
  if (fbaOnly) params.set("fbaOnly", "true");
  if (fbaOnly && fbaBucket !== "all") params.set("fbaStockHealth", fbaBucket);

  const { data: productsResult, isLoading } = useQuery({
    queryKey: [
      "products",
      unit,
      term,
      channelFilter,
      unreviewedOnly,
      brandFilter,
      fbaOnly,
      fbaBucket,
      outOfStockMarketplace,
    ],
    queryFn: () => apiWithMeta<Row[], { total: number }>(`/api/v1/products?${params.toString()}`),
  });

  const rows = productsResult?.data ?? [];
  // True total matching the current filter (not capped to this page) --
  // Isaac, 2026-09-19: "there should be a number of the total listings...
  // so i am able to see exactly how many listings there are when i select
  // the FBA only stock."
  const totalMatching = productsResult?.meta.total ?? 0;

  const invalidateProducts = () => void qc.invalidateQueries({ queryKey: ["products"] });

  const { data: summary } = useQuery({
    queryKey: ["inventory-summary"],
    queryFn: () => api<InventorySummary>("/api/v1/inventory/summary"),
    // Recount whenever a sync/match could plausibly have changed it, without
    // polling constantly on a page that's often left open in a background tab.
    staleTime: 60_000,
  });
  // Derived from the summary already fetched for the stat card, not a
  // separate GET /channels call -- that route requires the "marketplaces"
  // scope, and this page should work fully for an inventory-only user.
  //
  // Real bug found live 2026-09-23 (Isaac: "I don't see the Amazon button
  // here"): this used to read `summary.byChannel`, which despite the name is
  // actually the PENDING UNMATCHED count per channel, not active listings --
  // Amazon had 2,258 real active listings but zero pending-unmatched ones
  // that day, so it silently disappeared from the filter row entirely, the
  // one channel with the most inventory to filter by. `activeByMarketplace`
  // is the stat that actually means "does this channel have anything to
  // filter" -- already used correctly elsewhere on this page (the FBA-only
  // per-channel counts).
  const CHANNEL_FILTERS = Array.from(
    new Set(
      (summary?.activeByMarketplace ?? []).filter((c) => c.count > 0).map((c) => c.marketplace),
    ),
  );

  // Unmatched listings have no business-unit categorization yet (that's set
  // when a product record exists), and "low stock" is a reorder-point concept
  // they don't have either — showing them under a specific unit filter or
  // low-stock view would be presenting data the filter doesn't actually apply
  // to. "unlisted" is meaningless for them too -- an unmatched row is by
  // definition listed somewhere, just not matched to a product yet.
  // "out_of_stock" is equally meaningless here for the same reason (it's a
  // channel_listings.publishedQuantity concept, and an unmatched row has no
  // channel_listings row at all yet).
  const showUnmatched =
    unit === "all" && channelFilter !== "unlisted" && channelFilter !== "out_of_stock";
  // Real bug fixed 2026-08-29: this used to fetch the 200 globally-most-recent
  // pending rows and filter by channel client-side -- Amazon alone has 2,700+
  // pending rows synced slightly more recently than the other three, so
  // eBay/Walmart/Shopify were buried past the limit before the filter ever
  // ran, and their real inventory was never actually reachable on this page.
  // Filtering has to happen server-side, in the query itself.
  const [unmatchedPage, setUnmatchedPage] = useState(0);
  const UNMATCHED_PAGE_SIZE = 100;
  const unmatchedParams = new URLSearchParams({
    resolution: "pending",
    limit: String(UNMATCHED_PAGE_SIZE),
    offset: String(unmatchedPage * UNMATCHED_PAGE_SIZE),
  });
  if (term.trim()) unmatchedParams.set("q", term.trim());
  if (channelFilter !== "all") unmatchedParams.set("marketplace", channelFilter);
  const { data: unmatchedResult } = useQuery({
    queryKey: ["unmatched-for-inventory", term, channelFilter, unmatchedPage],
    queryFn: () =>
      apiWithMeta<UnmatchedRow[], { total: number }>(
        `/api/v1/unmatched-skus?${unmatchedParams.toString()}`,
      ),
    enabled: showUnmatched,
  });
  const unmatchedRows = showUnmatched ? (unmatchedResult?.data ?? []) : [];
  const unmatchedTotal = unmatchedResult?.meta.total ?? 0;

  // "Likely the same item across channels" -- Isaac's ask (2026-08-29):
  // detect a SKU live on multiple channels and let it be resolved once
  // instead of once per channel. Only meaningful on the unfiltered view (a
  // group by definition spans channels, so filtering to one channel would
  // just show fragments of groups).
  const showCrossChannel = channelFilter === "all" && unit === "all" && !term.trim();
  // Isaac, 2026-09-25: filtering to a marketplace that has no ASIN concept
  // (Walmart/eBay/Shopify each use their own id, never Amazon's) shouldn't
  // still show a column that's dashes for every single row on that view.
  const showAsin = !["walmart", "ebay", "shopify"].includes(channelFilter);
  const { data: crossChannelData } = useQuery({
    queryKey: ["unmatched-cross-channel"],
    queryFn: () => api<CrossChannelGroup[]>("/api/v1/unmatched-skus/cross-channel?limit=20"),
    enabled: showCrossChannel,
  });
  const crossChannelGroups = showCrossChannel ? (crossChannelData ?? []) : [];

  // Inbound-to-Amazon-FBA shipments (Isaac, 2026-09-19) -- the direct
  // companion to the FBA stock-health "Restock" badge: is a shipment
  // already on the way for this? Shipment-level, live-fetched, not tied to
  // the current filter/search (unlike crossChannelGroups above), since it
  // answers a channel-wide question, not a per-page one.
  const { data: inboundShipments } = useQuery({
    queryKey: ["fba-inbound-shipments"],
    queryFn: () => api<InboundShipment[]>("/api/v1/inventory/fba-inbound-shipments"),
    staleTime: 60_000,
  });

  /* ─────────────────────────── link listing (from a SKU, search listings) ─────────────────────────── */
  // Isaac's ask (2026-08-29): matching a SKU to a marketplace listing should
  // happen right here, not by navigating to Marketplaces and hunting for the
  // same row again. Search is debounced (300ms) rather than requiring an
  // Enter/click per keystroke -- Marketplaces.tsx's existing search is
  // manually-triggered; this is the more "flows properly" version Isaac
  // asked for, worth carrying back there later too.
  /* ─────────────────────────── bulk create SKUs from listings ─────────────────────────── */
  // The actual unblock for this account's real state (2026-08-29): 4,642
  // pending listings, ZERO products. Every "link to an existing SKU" flow
  // searched an empty table, and "Link listing" only renders on a product
  // row -- so with no products, none of it was reachable. Creating products
  // FROM listings, in bulk, is the step that has to come first.
  const [selectedUnmatched, setSelectedUnmatched] = useState<Set<string>>(new Set());
  const [bulkResult, setBulkResult] = useState<string | null>(null);

  /* ── bulk review step ──
   * The first version went straight from "Create N SKUs" to creating them,
   * silently adopting each marketplace SKU as the Ninjamo SKU. Isaac
   * (2026-08-29): "it should suggest a sku but not just create the
   * marketplace one." The button now opens a review screen where every
   * suggested SKU is editable and collisions are flagged before any write. */
  const [reviewOpen, setReviewOpen] = useState(false);
  const [skuDrafts, setSkuDrafts] = useState<Record<string, string>>({});
  const [skuPrefix, setSkuPrefix] = useState("");

  /** The suggestion before any human edit: the marketplace SKU normalized
   * into a valid Ninjamo SKU, optionally prefixed. One definition so the
   * review list and the reset affordance can't drift apart. */
  function suggestSku(channelSku: string, prefix: string): string {
    const base = channelSku
      .trim()
      .toUpperCase()
      .replace(/[^A-Z0-9._-]+/g, "-")
      .replace(/-{2,}/g, "-")
      .replace(/^[^A-Z0-9]+/, "")
      .replace(/[^A-Z0-9._-]+$/, "");
    return (prefix ? `${prefix}${base}` : base).slice(0, 64);
  }

  const reviewRows = unmatchedRows.filter((u) => selectedUnmatched.has(u.id));
  const effectiveSku = (u: UnmatchedRow) => skuDrafts[u.id] ?? suggestSku(u.channelSku, skuPrefix);
  const reviewSkus = reviewRows.map(effectiveSku);

  // Collisions against the real catalogue, plus duplicates within this batch
  // (two channels sharing a SKU would otherwise both try to create it).
  const { data: skuCheck } = useQuery({
    queryKey: ["sku-check", reviewSkus.join(",")],
    queryFn: () =>
      api<{ existing: string[]; normalized: Record<string, string> }>(
        `/api/v1/sku-check?skus=${encodeURIComponent(reviewSkus.join(","))}`,
      ),
    enabled: reviewOpen && reviewSkus.length > 0,
  });
  const existingSkus = new Set(skuCheck?.existing ?? []);
  const dupCounts = reviewSkus.reduce<Record<string, number>>((acc, s) => {
    acc[s] = (acc[s] ?? 0) + 1;
    return acc;
  }, {});

  const bulkCreate = useMutation({
    mutationFn: ({ ids, skus }: { ids: string[]; skus: Record<string, string> }) =>
      api<{
        created: number;
        linkedToExisting: number;
        siblingsResolved: number;
        skipped: unknown[];
      }>("/api/v1/unmatched-skus/bulk-create-products", {
        method: "POST",
        body: JSON.stringify({ ids, skus }),
      }),
    onSuccess: (r) => {
      setSelectedUnmatched(new Set());
      setSkuDrafts({});
      setSkuPrefix("");
      setReviewOpen(false);
      setBulkResult(
        t("inventory:bulkCreateResult", {
          created: r.created,
          linked: r.linkedToExisting,
          siblings: r.siblingsResolved,
          skipped: r.skipped.length,
        }),
      );
      invalidateMatching();
    },
    onError: (err) =>
      setBulkResult(err instanceof ApiClientError ? err.message : t("inventory:createFailed")),
  });

  function toggleUnmatchedSelection(id: string) {
    setSelectedUnmatched((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  /* ─────────────────────────── bulk licence review ─────────────────────────── */
  // Isaac, 2026-09-17: with only two real licences held (Street Fighter and
  // Mega Man), ~3,900 products sit at the honest "unreviewed" default -- and
  // the licence gate on POST /listings/push (correctly) blocks every one of
  // them from going live until reviewed. One-at-a-time review would never
  // clear that backlog, so this reviews whatever the CURRENT filter shows
  // (unit/search/channel/licence) in one write rather than one row at a time.
  // Deliberately two-step (open -> confirm) since this can touch hundreds of
  // rows -- same pattern as the delete-SKU confirm below.
  const [licenseBulkOpen, setLicenseBulkOpen] = useState(false);
  const [licenseBulkTarget, setLicenseBulkTarget] = useState<LicenseKind>("original");
  const [licenseBulkResult, setLicenseBulkResult] = useState<string | null>(null);
  const [licenseBulkError, setLicenseBulkError] = useState<string | null>(null);
  // Isaac, 2026-09-19: licence tooling (the "unreviewed only" filter and the
  // bulk-set button) was too prominent for something that isn't a daily
  // concern -- tucked behind a disclosure, off by default, rather than
  // always-visible controls in the main toolbar. The gate itself (still
  // enforced server-side on every push) is untouched -- this is UI weight
  // only.
  const [showLicenseTools, setShowLicenseTools] = useState(false);
  const bulkSetLicense = useMutation({
    mutationFn: () => {
      const body: Record<string, unknown> = { newLicenseKind: licenseBulkTarget };
      if (unit !== "all") body.businessUnit = unit;
      if (term.trim()) body.q = term.trim();
      if (channelFilter === "unlisted") body.listedOn = "unlisted";
      else if (channelFilter === "out_of_stock") {
        body.listedOn = "out_of_stock";
        if (outOfStockMarketplace !== "all") body.marketplace = outOfStockMarketplace;
      } else if (channelFilter !== "all") body.marketplace = channelFilter;
      if (unreviewedOnly) body.licenseKind = "unreviewed";
      return api<{ updated: number }>("/api/v1/products/bulk-license", {
        method: "POST",
        body: JSON.stringify(body),
      });
    },
    onSuccess: (data) => {
      setLicenseBulkOpen(false);
      setLicenseBulkError(null);
      setLicenseBulkResult(t("inventory:licenseBulkSuccess", { count: data.updated }));
      invalidateProducts();
    },
    onError: (err) =>
      setLicenseBulkError(
        err instanceof ApiClientError ? err.message : t("inventory:licenseBulkFailed"),
      ),
  });

  /* ─────────────────────────── list on a marketplace ─────────────────────────── */
  // Creates the draft channel_listings row (and its content-override surface)
  // so a SKU can be prepared for a channel before anything is pushed live.
  // Phase 2 (the actual push) now exists for Shopify -- see PUSHABLE_MARKETPLACES
  // and the pushListing mutation below; everything else still only gets this
  // internal record, with the not-pushed-live banner on the edit dialog.
  const [listingProduct, setListingProduct] = useState<{ id: string; sku: string } | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const listOnChannel = useMutation({
    mutationFn: ({ sku, marketplace }: { sku: string; marketplace: string }) =>
      api("/api/v1/listings", {
        method: "PUT",
        body: JSON.stringify({ sku, marketplace, accountLabel: "primary", status: "draft" }),
      }),
    onSuccess: () => {
      invalidateMatching();
      setListingProduct(null);
    },
    onError: (err) =>
      setListError(err instanceof ApiClientError ? err.message : t("inventory:listingSaveFailed")),
  });

  const [crossChannelOpen, setCrossChannelOpen] = useState(false);
  const [inboundShipmentsOpen, setInboundShipmentsOpen] = useState(false);

  /* ─────────────────────────── edit / delete a Ninjamo SKU ─────────────────────────── */
  // Renaming is safe by construction: listings, stock and order lines all
  // reference the product's stable internal id, never the SKU string, so a
  // rename keeps every channel attached (verified against the DB before this
  // was exposed). The dialog shows that internal id so it's visible that the
  // SKU is a label, not the identity.
  const [editingProduct, setEditingProduct] = useState<Row | null>(null);
  const [editSku, setEditSku] = useState("");
  const [editProductTitle, setEditProductTitle] = useState("");
  const [editProductError, setEditProductError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editStockMode, setEditStockMode] = useState<"tracked" | "made_to_order">("tracked");
  const [editMtoQty, setEditMtoQty] = useState("10");
  const [editOnHand, setEditOnHand] = useState("");

  function openEditProduct(r: Row) {
    setEditingProduct(r);
    setEditSku(r.sku);
    setEditProductTitle(r.title);
    setEditProductError(null);
    setConfirmDelete(false);
    setEditStockMode(r.stockMode);
    setEditMtoQty(String(r.madeToOrderQuantity));
    setEditOnHand(String(r.onHand));
  }

  /** Sets warehouse on-hand to an absolute number — the server turns that
   * into the right ledger adjustment, so the movement history stays honest
   * while the operator just types the count they actually have. */
  const setStock = useMutation({
    mutationFn: ({ sku, onHand }: { sku: string; onHand: number }) =>
      api("/api/v1/stock/set", { method: "POST", body: JSON.stringify({ sku, onHand }) }),
    onSuccess: () => invalidateMatching(),
    onError: (err) =>
      setEditProductError(
        err instanceof ApiClientError ? err.message : t("inventory:movementFailed"),
      ),
  });

  const saveProduct = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      api(`/api/v1/products/${encodeURIComponent(id)}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      invalidateMatching();
      setEditingProduct(null);
    },
    onError: (err) =>
      setEditProductError(
        err instanceof ApiClientError ? err.message : t("inventory:archiveFailed"),
      ),
  });

  const deleteProduct = useMutation({
    mutationFn: ({ id, force }: { id: string; force: boolean }) =>
      api(`/api/v1/products/${encodeURIComponent(id)}${force ? "?force=true" : ""}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      invalidateMatching();
      setEditingProduct(null);
      setExpandedSku(null);
    },
    onError: (err) =>
      setEditProductError(
        err instanceof ApiClientError ? err.message : t("inventory:deleteFailed"),
      ),
  });

  const unlinkListing = useMutation({
    mutationFn: (listingId: string) =>
      api(`/api/v1/listings/${encodeURIComponent(listingId)}`, { method: "DELETE" }),
    onSuccess: () => invalidateMatching(),
  });

  const [linkingProduct, setLinkingProduct] = useState<{ id: string; sku: string } | null>(null);
  const [linkSearch, setLinkSearch] = useState("");
  const [linkChannel, setLinkChannel] = useState<string>("all");
  const linkSearchDebounced = useDebounced(linkSearch, 300);
  const linkParams = new URLSearchParams({ resolution: "pending", limit: "8" });
  if (linkSearchDebounced.trim()) linkParams.set("q", linkSearchDebounced.trim());
  if (linkChannel !== "all") linkParams.set("marketplace", linkChannel);
  const { data: linkResults, isFetching: linkSearching } = useQuery({
    queryKey: ["link-listing-search", linkSearchDebounced, linkChannel],
    queryFn: () => api<UnmatchedRow[]>(`/api/v1/unmatched-skus?${linkParams.toString()}`),
    enabled: linkingProduct !== null,
  });

  const invalidateMatching = () => {
    invalidateProducts();
    void qc.invalidateQueries({ queryKey: ["unmatched-for-inventory"] });
    void qc.invalidateQueries({ queryKey: ["unmatched-cross-channel"] });
    void qc.invalidateQueries({ queryKey: ["inventory-summary"] });
    if (expandedSku) void qc.invalidateQueries({ queryKey: ["product-detail", expandedSku] });
  };

  const linkListingMutation = useMutation({
    mutationFn: ({ unmatchedId, productId }: { unmatchedId: string; productId: string }) =>
      api(`/api/v1/unmatched-skus/${unmatchedId}/map`, {
        method: "POST",
        body: JSON.stringify({ productId }),
      }),
    onSuccess: () => {
      invalidateMatching();
      setLinkingProduct(null);
      setLinkSearch("");
    },
  });

  /* ─────────────────────────── resolve unmatched (from a listing, search or create SKU) ─────────────────────────── */
  const [resolvingListing, setResolvingListing] = useState<{
    id: string;
    channelSku: string;
    title: string | null;
    price: string | null;
  } | null>(null);
  const [resolveMode, setResolveMode] = useState<"link" | "create">("link");
  const [resolveSearch, setResolveSearch] = useState("");
  const resolveSearchDebounced = useDebounced(resolveSearch, 300);
  const { data: resolveResults } = useQuery({
    queryKey: ["resolve-product-search", resolveSearchDebounced],
    queryFn: () =>
      api<Array<{ id: string; sku: string; title: string }>>(
        `/api/v1/product-search?q=${encodeURIComponent(resolveSearchDebounced.trim())}`,
      ),
    enabled: resolvingListing !== null && resolveSearchDebounced.trim().length > 0,
  });
  const [resolveNewSku, setResolveNewSku] = useState("");
  const [resolveNewTitle, setResolveNewTitle] = useState("");
  const [resolveNewUnit, setResolveNewUnit] = useState<Unit>("stickers");
  const [resolveNewKind, setResolveNewKind] = useState<ProductKind>("sticker_single");
  const [resolveNewLicense, setResolveNewLicense] = useState<LicenseKind>("unreviewed");
  const [resolveError, setResolveError] = useState<string | null>(null);

  function openResolve(listing: {
    id: string;
    channelSku: string;
    title: string | null;
    price: string | null;
  }) {
    setResolvingListing(listing);
    setResolveMode("link");
    setResolveSearch("");
    setResolveError(null);
    setResolveNewSku(sanitizeSku(listing.channelSku));
    setResolveNewTitle(listing.title ?? "");
    setResolveNewUnit("stickers");
    setResolveNewKind("sticker_single");
    setResolveNewLicense("unreviewed");
  }

  const resolveLinkMutation = useMutation({
    mutationFn: ({ unmatchedId, productId }: { unmatchedId: string; productId: string }) =>
      api(`/api/v1/unmatched-skus/${unmatchedId}/map`, {
        method: "POST",
        body: JSON.stringify({ productId }),
      }),
    onSuccess: () => {
      invalidateMatching();
      setResolvingListing(null);
    },
    onError: (err) =>
      setResolveError(
        err instanceof ApiClientError ? err.message : t("inventory:listingSaveFailed"),
      ),
  });
  const resolveCreateMutation = useMutation({
    mutationFn: ({ unmatchedId, body }: { unmatchedId: string; body: Record<string, unknown> }) =>
      api(`/api/v1/unmatched-skus/${unmatchedId}/create-product`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      invalidateMatching();
      setResolvingListing(null);
    },
    onError: (err) =>
      setResolveError(err instanceof ApiClientError ? err.message : t("inventory:createFailed")),
  });

  function handleResolveCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!resolvingListing) return;
    setResolveError(null);
    resolveCreateMutation.mutate({
      unmatchedId: resolvingListing.id,
      body: {
        sku: sanitizeSku(resolveNewSku) || undefined,
        title: resolveNewTitle.trim(),
        businessUnit: resolveNewUnit,
        kind: resolveNewKind,
        licenseKind: resolveNewLicense,
      },
    });
  }

  // Expandable per-SKU channel breakdown — fetched on demand, not batched
  // with the list, so a 3,500-row unmatched queue never triggers a fan-out.
  const [expandedSku, setExpandedSku] = useState<string | null>(null);
  const { data: detail } = useQuery({
    queryKey: ["product-detail", expandedSku],
    queryFn: () => api<ProductDetail>(`/api/v1/products/${encodeURIComponent(expandedSku ?? "")}`),
    enabled: expandedSku !== null,
  });
  const { data: runRate } = useQuery({
    queryKey: ["product-run-rate", expandedSku],
    queryFn: () =>
      api<RunRate>(`/api/v1/products/${encodeURIComponent(expandedSku ?? "")}/run-rate?period=30d`),
    enabled: expandedSku !== null,
  });
  const { data: profit } = useQuery({
    queryKey: ["product-profit", expandedSku],
    queryFn: () =>
      api<ProfitDetail>(
        `/api/v1/products/${encodeURIComponent(expandedSku ?? "")}/profit?period=30d`,
      ),
    enabled: expandedSku !== null,
  });
  // Informational only (never blocks a save) -- independent per-channel
  // allocation was the confirmed model, but two channels both claiming a
  // large share of the same pooled stock is still worth flagging.
  const allocationSum = (detail?.listings ?? []).reduce(
    (sum, l) => sum + (l.allocationPercent != null ? Number(l.allocationPercent) : 0),
    0,
  );

  /* ─────────────────────────── channel listing edit ─────────────────────────── */

  const [editingChannel, setEditingChannel] = useState<ListingDetail | null>(null);
  const [editPrice, setEditPrice] = useState("");
  const [editMode, setEditMode] = useState<"manual" | "allocation">("manual");
  const [editQty, setEditQty] = useState("");
  const [editAllocation, setEditAllocation] = useState("");
  const [editTitle, setEditTitle] = useState("");
  const [editBullets, setEditBullets] = useState("");
  const [editDescription, setEditDescription] = useState("");
  // Unlike title/bullets/description (empty box, effective value shown only
  // as placeholder), this one is actually PRE-FILLED with a real value --
  // Isaac, 2026-09-22, after seeing his own real Amazon listings use short
  // subject labels ("Friendly Animals", "Certified Fishing") that a
  // mechanical rule can't reliably guess: wants a suggestion sitting in the
  // box ready to launch with or quickly edit, not a blank field to fill in
  // from scratch every time.
  const [editSubjectCharacter, setEditSubjectCharacter] = useState("");
  const [subjectCharacterTouched, setSubjectCharacterTouched] = useState(false);
  // Color/theme/special features/keywords: unlike subject character, Isaac
  // ("I can always review after", 2026-09-22) doesn't need these reviewed
  // BEFORE a launch -- pushListing already has safe generic defaults for
  // every one of them. So these stay empty-box-with-placeholder, same as
  // title/bullets/description: nothing to fill in to launch, just a place
  // to fix a default afterward if it doesn't fit a given design.
  const [editColor, setEditColor] = useState("");
  const [editTheme, setEditTheme] = useState("");
  const [editSpecialFeatures, setEditSpecialFeatures] = useState("");
  const [editGenericKeywords, setEditGenericKeywords] = useState("");
  const [editError, setEditError] = useState<string | null>(null);

  const { data: contentOverride } = useQuery({
    queryKey: ["listing-content", expandedSku, editingChannel?.marketplace],
    queryFn: () =>
      api<ContentOverride>(
        `/api/v1/listings/content?sku=${encodeURIComponent(expandedSku ?? "")}&marketplace=${editingChannel?.marketplace}&accountLabel=${encodeURIComponent(editingChannel?.accountLabel ?? "primary")}`,
      ),
    enabled: editingChannel !== null && expandedSku !== null,
  });

  // Fills editSubjectCharacter once the effective content loads: the
  // reviewed value if one was already saved, otherwise a fresh suggestion
  // from the effective title. Guarded by subjectCharacterTouched so it
  // doesn't clobber whatever the person is actively typing.
  useEffect(() => {
    if (!editingChannel || subjectCharacterTouched || !contentOverride) return;
    const saved = contentOverride.effective.fields?.subjectCharacter;
    setEditSubjectCharacter(
      typeof saved === "string" && saved.trim()
        ? saved
        : (suggestSubjectCharacter(contentOverride.effective.title) ?? ""),
    );
  }, [editingChannel, contentOverride, subjectCharacterTouched]);

  function openEdit(listing: ListingDetail) {
    setEditingChannel(listing);
    setEditError(null);
    setEditPrice(listing.price ?? "");
    setEditMode(listing.allocationPercent != null ? "allocation" : "manual");
    setEditQty(listing.publishedQuantity != null ? String(listing.publishedQuantity) : "");
    setEditAllocation(listing.allocationPercent ?? "");
    setEditTitle("");
    setEditBullets("");
    setEditDescription("");
    setEditSubjectCharacter("");
    setSubjectCharacterTouched(false);
    setEditColor("");
    setEditTheme("");
    setEditSpecialFeatures("");
    setEditGenericKeywords("");
    setConfirmPublish(false);
    setPushError(null);
    setPushSuccessMessage(null);
    setPushWarning(null);
    setConfirmDeleteRemote(false);
    setDeleteRemoteError(null);
    setImagesError(null);
    setImagesSuccess(null);
  }

  const saveListing = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api("/api/v1/listings", { method: "PUT", body: JSON.stringify(body) }),
  });
  const saveContent = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api("/api/v1/listings/content", { method: "PUT", body: JSON.stringify(body) }),
  });

  /* ─────────────────────────── push a listing live (Phase 2) ─────────────────────────── */
  const [confirmPublish, setConfirmPublish] = useState(false);
  const [pushError, setPushError] = useState<string | null>(null);
  // Distinct from the persistent "linked listing" banner above, which can be
  // true purely from an ordinary read-only sync matching an existing
  // Shopify product to this SKU -- this only ever shows right after THIS
  // app's own push mutation actually completed, so it's unambiguous proof
  // (found necessary 2026-09-17: Isaac tested a SKU that already had a
  // pre-existing matched listing, and the persistent banner alone read as
  // confirmation of a push that had never actually happened).
  const [pushSuccessMessage, setPushSuccessMessage] = useState<string | null>(null);
  // Amazon-only: title-length and similar non-fatal notices from the push
  // itself. Shopify's own `warning` field is deliberately never shown here
  // (see this mutation's onSuccess for why -- it's a known false-positive
  // noise source, not a real problem to surface).
  const [pushWarning, setPushWarning] = useState<string | null>(null);
  const pushListing = useMutation({
    mutationFn: ({ status }: { status: "draft" | "active" }) => {
      if (!editingChannel || !expandedSku) throw new Error("no listing selected");
      return api<{
        externalId: string | null;
        marketplaceId: string | null;
        status: "draft" | "pending" | "active" | "suppressed" | "ended";
        url: string | null;
        warning: string | null;
      }>("/api/v1/listings/push", {
        method: "POST",
        body: JSON.stringify({
          sku: expandedSku,
          marketplace: editingChannel.marketplace,
          accountLabel: editingChannel.accountLabel,
          status,
        }),
      });
    },
    onSuccess: (data, variables) => {
      setConfirmPublish(false);
      setPushError(null);
      setPushWarning(editingChannel?.marketplace === "amazon" ? (data.warning ?? null) : null);
      // The server also tries (and, without a scope only Amir can grant,
      // reliably fails) to attach the product to the Online Store channel
      // directly -- `data.warning` reports that attempt. NOT surfaced here
      // on purpose: a Shopify Flow automation (built 2026-09-17) reliably
      // does the real publish a moment later regardless, confirmed live
      // twice, so showing this warning every single push was pure noise
      // that only confused Isaac ("why do I keep getting this?") about an
      // outcome that was actually fine. See PushListingResult.warning's own
      // comment for the full story if this needs revisiting.
      setPushSuccessMessage(
        t(
          variables.status === "active"
            ? "inventory:pushSuccessLive"
            : "inventory:pushSuccessDraft",
          {
            channel: editingChannel?.marketplace,
          },
        ),
      );
      // Reconcile the OPEN dialog's own state immediately, rather than
      // waiting on the invalidated query below to refetch and happen to
      // still be mounted to receive it -- this is exactly what was showing
      // the stale "not pushed yet" banner right after a push that had, in
      // fact, just succeeded (2026-09-17, caught mid-testing).
      setEditingChannel((prev) =>
        prev
          ? {
              ...prev,
              externalId: data.externalId,
              marketplaceId: data.marketplaceId,
              status: data.status,
            }
          : prev,
      );
      void qc.invalidateQueries({ queryKey: ["product-detail", expandedSku] });
      invalidateProducts();
    },
    onError: (err) => {
      setConfirmPublish(false);
      setPushSuccessMessage(null);
      setPushWarning(null);
      setPushError(err instanceof ApiClientError ? err.message : t("inventory:pushFailed"));
    },
  });

  /* ─────────────────────── delete a listing from the channel itself ─────────────────────── */
  // Distinct confirm step from `confirmPublish` above -- irreversible on the
  // channel's side (unlike a draft/live push, which can always be re-pushed),
  // so this gets its own explicit "are you sure" rather than reusing that
  // one's styling for a much higher-stakes action.
  const [confirmDeleteRemote, setConfirmDeleteRemote] = useState(false);
  const [deleteRemoteError, setDeleteRemoteError] = useState<string | null>(null);
  const deleteRemoteListing = useMutation({
    mutationFn: () => {
      if (!editingChannel) throw new Error("no listing selected");
      return api(`/api/v1/listings/${editingChannel.id}/delete-remote`, { method: "POST" });
    },
    onSuccess: () => {
      setConfirmDeleteRemote(false);
      setDeleteRemoteError(null);
      setEditingChannel(null);
      void qc.invalidateQueries({ queryKey: ["product-detail", expandedSku] });
      invalidateProducts();
    },
    onError: (err) => {
      setConfirmDeleteRemote(false);
      setDeleteRemoteError(
        err instanceof ApiClientError ? err.message : t("inventory:deleteRemoteFailed"),
      );
    },
  });

  /* ─────────────────────────── fetch content from another channel ─────────────────────────── */
  // Isaac, 2026-09-17: "all of the images ... belong to us so it doesn't
  // matter what platform they come from" -- pulls images AND description
  // from wherever this product already has real content (Amazon today) into
  // the one shared products.images/products.description, so a channel
  // that's never had a photo or real copy (Shopify) can launch with the
  // real thing instead of none.
  const [imagesError, setImagesError] = useState<string | null>(null);
  const [imagesSuccess, setImagesSuccess] = useState<string | null>(null);
  const fetchImages = useMutation({
    mutationFn: () => {
      if (!expandedSku) throw new Error("no product selected");
      return api<{ images: string[]; description: string | null; source: string }>(
        `/api/v1/products/${encodeURIComponent(expandedSku)}/fetch-content`,
        { method: "POST" },
      );
    },
    onSuccess: (data) => {
      setImagesError(null);
      setImagesSuccess(
        data.description
          ? t("inventory:contentFetchSuccessWithDescription", {
              count: data.images.length,
              source: data.source,
            })
          : t("inventory:imagesFetchSuccess", { count: data.images.length, source: data.source }),
      );
      void qc.invalidateQueries({ queryKey: ["product-detail", expandedSku] });
      // The Description textarea's placeholder reads the product's default
      // via this "effective content" query, not product-detail directly --
      // without this, a fetched description wouldn't show until the dialog
      // was closed and reopened, same class of bug the push-banner fix above
      // already caught once this session.
      void qc.invalidateQueries({ queryKey: ["listing-content"] });
      invalidateProducts();
    },
    onError: (err) => {
      setImagesSuccess(null);
      setImagesError(
        err instanceof ApiClientError ? err.message : t("inventory:imagesFetchFailed"),
      );
    },
  });

  // Manual upload for a product that already exists -- same endpoint the
  // "Add product" form's upload uses, just a second entry point onto it
  // (Isaac, 2026-09-22: needed a way to add/replace a photo on an existing
  // SKU, not just at creation time).
  const uploadExistingImage = useMutation({
    mutationFn: (file: File) => {
      if (!expandedSku) throw new Error("no product selected");
      const formData = new FormData();
      formData.append("file", file);
      return api<{ images: string[] }>(
        `/api/v1/products/${encodeURIComponent(expandedSku)}/image`,
        { method: "POST", body: formData },
      );
    },
    onSuccess: () => {
      setImagesError(null);
      setImagesSuccess(t("inventory:imageUploadSuccess"));
      void qc.invalidateQueries({ queryKey: ["product-detail", expandedSku] });
      invalidateProducts();
    },
    onError: (err) => {
      setImagesSuccess(null);
      setImagesError(
        err instanceof ApiClientError ? err.message : t("inventory:imageUploadFailed"),
      );
    },
  });

  async function handleSaveListing() {
    if (!editingChannel || !expandedSku) return;
    setEditError(null);
    try {
      const body: Record<string, unknown> = {
        sku: expandedSku,
        marketplace: editingChannel.marketplace,
        accountLabel: editingChannel.accountLabel,
      };
      if (editPrice.trim()) body.price = editPrice.trim();
      if (editMode === "manual") {
        if (editQty.trim()) body.publishedQuantity = Number(editQty.trim());
      } else if (editAllocation.trim()) {
        body.allocationPercent = editAllocation.trim();
      }
      await saveListing.mutateAsync(body);

      if (
        editTitle.trim() ||
        editBullets.trim() ||
        editDescription.trim() ||
        editSubjectCharacter.trim() ||
        editColor.trim() ||
        editTheme.trim() ||
        editSpecialFeatures.trim() ||
        editGenericKeywords.trim()
      ) {
        const contentBody: Record<string, unknown> = {
          sku: expandedSku,
          marketplace: editingChannel.marketplace,
          accountLabel: editingChannel.accountLabel,
        };
        if (editTitle.trim()) contentBody.title = editTitle.trim();
        if (editBullets.trim())
          contentBody.bullets = editBullets
            .split("\n")
            .map((b) => b.trim())
            .filter(Boolean);
        if (editDescription.trim()) contentBody.description = editDescription.trim();
        // `fields` is replaced wholesale server-side, so merge onto whatever
        // was already there rather than overwriting other keys future work
        // might store here (Amazon backend keywords, eBay item specifics).
        const fieldEdits: Record<string, unknown> = {};
        if (editSubjectCharacter.trim()) fieldEdits.subjectCharacter = editSubjectCharacter.trim();
        if (editColor.trim()) fieldEdits.color = editColor.trim();
        if (editTheme.trim()) fieldEdits.theme = editTheme.trim();
        if (editSpecialFeatures.trim())
          fieldEdits.specialFeatures = editSpecialFeatures
            .split(",")
            .map((v) => v.trim())
            .filter(Boolean);
        if (editGenericKeywords.trim())
          fieldEdits.genericKeywords = editGenericKeywords
            .split(",")
            .map((v) => v.trim())
            .filter(Boolean);
        if (Object.keys(fieldEdits).length > 0) {
          contentBody.fields = { ...(contentOverride?.effective.fields ?? {}), ...fieldEdits };
        }
        await saveContent.mutateAsync(contentBody);
      }

      void qc.invalidateQueries({ queryKey: ["product-detail", expandedSku] });
      void qc.invalidateQueries({ queryKey: ["listing-content"] });
      invalidateProducts();
      setEditingChannel(null);
    } catch (err) {
      setEditError(err instanceof ApiClientError ? err.message : t("inventory:listingSaveFailed"));
    }
  }

  /* ─────────────────────────── add product ─────────────────────────── */

  /* ─────────────────────── standalone "Create SKU" (Bin Manager) ─────────────────────── */
  // Isaac, 2026-09-24: deliberately independent of the "Add product" flow
  // below and of this app's own product catalog entirely -- no Ninjamo
  // product is created or required. Mirrors Bin Manager's own "Register new
  // SKU" form field-for-field; see createBinManagerSkuSchema's own comment.
  const [createSkuOpen, setCreateSkuOpen] = useState(false);
  const [csCategory, setCsCategory] = useState("21");
  const [csCategoryCustom, setCsCategoryCustom] = useState("");
  const [csBrand, setCsBrand] = useState<Brand>("ninjamo");
  const [csModel, setCsModel] = useState("");
  const [csUpc, setCsUpc] = useState("");
  const [csDescription, setCsDescription] = useState("");
  const [csEstimatedRetail, setCsEstimatedRetail] = useState("");
  const [csAvgCost, setCsAvgCost] = useState("");
  const [csError, setCsError] = useState<string | null>(null);
  const [csResult, setCsResult] = useState<{
    binManagerSku: string;
    warning: string | null;
  } | null>(null);

  function resetCreateSkuForm() {
    setCsCategory("21");
    setCsCategoryCustom("");
    setCsBrand("ninjamo");
    setCsModel("");
    setCsUpc("");
    setCsDescription("");
    setCsEstimatedRetail("");
    setCsAvgCost("");
    setCsError(null);
    setCsResult(null);
  }

  const createBinManagerSkuMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api<{ binManagerSku: string; warning: string | null }>("/api/v1/binmanager/create-sku", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: (res) => {
      setCsResult(res);
      setCsError(null);
    },
    onError: (err) =>
      setCsError(err instanceof ApiClientError ? err.message : t("inventory:binManagerPushFailed")),
  });

  function handleCreateBinManagerSku(e: React.FormEvent) {
    e.preventDefault();
    setCsError(null);
    setCsResult(null);
    const categoryId = Number.parseInt(csCategory === "custom" ? csCategoryCustom : csCategory, 10);
    if (!Number.isFinite(categoryId)) {
      setCsError(t("inventory:binManagerCategoryIdPlaceholder"));
      return;
    }
    const body: Record<string, unknown> = {
      categoryId,
      brand: csBrand,
      model: csModel.trim(),
      description: csDescription.trim(),
      estimatedRetail: csEstimatedRetail.trim(),
    };
    if (csAvgCost.trim()) body.avgCost = csAvgCost.trim();
    if (csUpc.trim()) body.upc = csUpc.trim();
    createBinManagerSkuMutation.mutate(body);
  }

  const [addOpen, setAddOpen] = useState(false);
  const [newSku, setNewSku] = useState("");
  const [newTitle, setNewTitle] = useState("");
  const [newUnit, setNewUnit] = useState<Unit>("stickers");
  const [newKind, setNewKind] = useState<ProductKind>("sticker_single");
  // Isaac, 2026-09-22: Amazon's own listing form has no "licence" input at
  // all, and nearly every sticker ships under Ninjamo or Peeker -- an
  // unreviewed-IP review step is the rare exception, not the common case, so
  // this simple form asks for the thing that's actually always true (which
  // brand) instead. `licenseKind` still exists underneath (drives the
  // active-push safety gate) but defaults to "original" here rather than
  // asking -- the rare licensed/unreviewed case is a "Licence tools" job,
  // not a Quick Launch one.
  const [newBrand, setNewBrand] = useState<Brand>("ninjamo");
  const [newListPrice, setNewListPrice] = useState("");
  const [newLandedCost, setNewLandedCost] = useState("");
  // Package dimensions -- Isaac, 2026-09-24: "i believe amazon requires it
  // and it may help with your descriptions... it should give them an option
  // when they click add product." All optional (same reasoning as the Bin
  // Manager fields above: not every product needs this filled in). When
  // set, these override amazon.ts's flat fallback constant on push.
  const [newLengthIn, setNewLengthIn] = useState("");
  const [newWidthIn, setNewWidthIn] = useState("");
  const [newHeightIn, setNewHeightIn] = useState("");
  const [newWeightLb, setNewWeightLb] = useState("");
  const [newQty, setNewQty] = useState("");
  const [newImageFile, setNewImageFile] = useState<File | null>(null);
  const newImageInputRef = useRef<HTMLInputElement>(null);
  const [createError, setCreateError] = useState<string | null>(null);

  const createProduct = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api("/api/v1/products", { method: "POST", body: JSON.stringify(body) }),
  });

  // Just the one main photo (Isaac, 2026-09-21) -- same convention the
  // Amazon/eBay auto-fetch paths already follow. Separate mutation, not
  // folded into createProduct's body, since this is a real file upload
  // (FormData) against a different endpoint than the plain JSON product
  // create -- see server/routes/inventory.ts's own doc comment on this route
  // for the pending bucket-policy prerequisite.
  const uploadProductImage = useMutation({
    mutationFn: ({ sku, file }: { sku: string; file: File }) => {
      const formData = new FormData();
      formData.append("file", file);
      return api(`/api/v1/products/${encodeURIComponent(sku)}/image`, {
        method: "POST",
        body: formData,
      });
    },
  });

  const canCreate = newSku.trim().length >= 2 && newTitle.trim().length >= 2;

  function resetAddProductForm() {
    setNewSku("");
    setNewTitle("");
    setNewListPrice("");
    setNewLandedCost("");
    setNewLengthIn("");
    setNewWidthIn("");
    setNewHeightIn("");
    setNewWeightLb("");
    setNewQty("");
    setNewBrand("ninjamo");
    setNewImageFile(null);
    if (newImageInputRef.current) newImageInputRef.current.value = "";
    setCreateError(null);
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setCreateError(null);
    const body: Record<string, unknown> = {
      // Isaac, 2026-09-23: exactly what's typed, not force-uppercased or
      // stripped -- `sanitizeSku` still exists for turning a messy CHANNEL
      // sku into a suggested internal one (the resolve-unmatched-sku flow
      // below), which is a different job than this form's own free-typed
      // SKU field.
      sku: newSku.trim(),
      title: newTitle.trim(),
      businessUnit: newUnit,
      kind: newKind,
      brand: newBrand,
      // Every Quick Launch product is Ninjamo/Peeker's own branded artwork
      // (Isaac, 2026-09-22) -- the rare licensed/unreviewed exception is a
      // "Licence tools" job, not something this simple form asks about.
      licenseKind: "original",
    };
    if (newListPrice.trim()) body.listPrice = newListPrice.trim();
    if (newLandedCost.trim()) body.landedCost = newLandedCost.trim();
    if (newLengthIn.trim()) body.lengthIn = newLengthIn.trim();
    if (newWidthIn.trim()) body.widthIn = newWidthIn.trim();
    if (newHeightIn.trim()) body.heightIn = newHeightIn.trim();
    if (newWeightLb.trim()) body.weightLb = newWeightLb.trim();

    try {
      await createProduct.mutateAsync(body);
      const qty = Number(newQty);
      if (Number.isFinite(qty) && qty > 0) {
        await postMovement.mutateAsync({
          sku: body.sku,
          // All of Ninjamo's stickers ship from the one San Diego warehouse
          // (Isaac, 2026-09-22) -- nothing to ask here, "main" is the only
          // location that's ever actually used.
          location: "main",
          kind: "receipt",
          quantity: qty,
          reference: t("inventory:initialStockReference"),
        });
      }
      if (newImageFile) {
        await uploadProductImage.mutateAsync({ sku: body.sku as string, file: newImageFile });
      }
      invalidateProducts();
      setAddOpen(false);
      resetAddProductForm();
    } catch (err) {
      setCreateError(err instanceof ApiClientError ? err.message : t("inventory:createFailed"));
    }
  }

  /* ─────────────────────────── adjust stock ─────────────────────────── */

  const adjustRef = useRef<HTMLDivElement>(null);
  const [adjustSku, setAdjustSku] = useState("");
  const [adjustKind, setAdjustKind] = useState<MovementKind>("receipt");
  const [adjustDirection, setAdjustDirection] = useState<"add" | "remove">("add");
  const [adjustQty, setAdjustQty] = useState("");
  const [adjustLocation, setAdjustLocation] = useState("main");
  const [adjustReference, setAdjustReference] = useState("");
  const [adjustNote, setAdjustNote] = useState("");
  const [adjustError, setAdjustError] = useState<string | null>(null);

  const postMovement = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api("/api/v1/stock/movements", { method: "POST", body: JSON.stringify(body) }),
  });

  function openAdjust(sku: string) {
    setAdjustSku(sku);
    setAdjustError(null);
    adjustRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }

  async function handleAdjust(e: React.FormEvent) {
    e.preventDefault();
    setAdjustError(null);
    const magnitude = Math.trunc(Number(adjustQty));
    if (!adjustSku || !Number.isFinite(magnitude) || magnitude <= 0) return;
    const quantity = adjustDirection === "remove" ? -magnitude : magnitude;

    try {
      await postMovement.mutateAsync({
        sku: adjustSku,
        location: adjustLocation.trim() || "main",
        kind: adjustKind,
        quantity,
        reference: adjustReference.trim() || undefined,
        note: adjustNote.trim() || undefined,
      });
      invalidateProducts();
      setAdjustQty("");
      setAdjustReference("");
      setAdjustNote("");
    } catch (err) {
      setAdjustError(err instanceof ApiClientError ? err.message : t("inventory:movementFailed"));
    }
  }

  /* ─────────────────────────── archive / restore ─────────────────────────── */

  const [statusError, setStatusError] = useState<string | null>(null);

  const setProductStatus = useMutation({
    mutationFn: ({ sku, status }: { sku: string; status: string }) =>
      api(`/api/v1/products/${encodeURIComponent(sku)}`, {
        method: "PATCH",
        body: JSON.stringify({ status }),
      }),
    onSuccess: invalidateProducts,
    onError: (err) =>
      setStatusError(err instanceof ApiClientError ? err.message : t("inventory:archiveFailed")),
  });

  const resolveRestockFlag = useMutation({
    mutationFn: ({ id, action }: { id: string; action: "acknowledge" | "dismiss" }) =>
      api(`/api/v1/amazon-health-flags/${id}/${action}`, { method: "POST" }),
    onSuccess: invalidateProducts,
  });

  return (
    <>
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1>{t("navigation:inventory")}</h1>
          <p className="mt-2 text-muted-foreground">{t("inventory:subtitle")}</p>
        </div>
        <span className="inline-flex gap-2">
          <Button type="button" variant="outline" onClick={() => setCreateSkuOpen((v) => !v)}>
            <Plus className="h-4 w-4" aria-hidden />
            {t("inventory:createSku")}
          </Button>
          <Button type="button" onClick={() => setAddOpen((v) => !v)}>
            <Plus className="h-4 w-4" aria-hidden />
            {t("inventory:addProduct")}
          </Button>
        </span>
      </div>

      {/* Isaac, 2026-09-24: "Create SKU" (a standalone push straight to Bin
          Manager, no Ninjamo product involved) and "Add product" (this
          app's own catalog) are deliberately two separate, independent
          cards, not one folded into the other -- and shown "in the same
          field of view", side by side, rather than one above the other
          requiring a scroll. */}
      {(createSkuOpen || addOpen) && (
        <div className="mb-6 grid grid-cols-1 gap-6 xl:grid-cols-2">
          {createSkuOpen && (
            <Card>
              <CardHeader className="p-4 pb-2">
                <CardTitle className="text-base">{t("inventory:createSku")}</CardTitle>
                <CardDescription>{t("inventory:createSkuHint")}</CardDescription>
              </CardHeader>
              <CardContent className="p-4 pt-0">
                <form
                  onSubmit={handleCreateBinManagerSku}
                  className="grid grid-cols-1 gap-3 sm:grid-cols-2"
                >
                  <div>
                    <Label htmlFor="cs-category">{t("inventory:binManagerCategory")}</Label>
                    <Select value={csCategory} onValueChange={setCsCategory}>
                      <SelectTrigger id="cs-category" className="mt-1 w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {BINMANAGER_CATEGORY_OPTIONS.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {csCategory === "custom" && (
                      <Input
                        type="number"
                        step="1"
                        min="1"
                        value={csCategoryCustom}
                        onChange={(e) => setCsCategoryCustom(e.target.value)}
                        placeholder={t("inventory:binManagerCategoryIdPlaceholder")}
                        className="mt-1.5"
                      />
                    )}
                  </div>
                  <div>
                    <Label htmlFor="cs-brand">{t("inventory:brandLabel")}</Label>
                    <Select value={csBrand} onValueChange={(v) => setCsBrand(v as Brand)}>
                      <SelectTrigger id="cs-brand" className="mt-1 w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ninjamo">{t("inventory:brand.ninjamo")}</SelectItem>
                        <SelectItem value="peeker">{t("inventory:brand.peeker")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="cs-model">{t("inventory:modelNumber")}</Label>
                    <Input
                      id="cs-model"
                      value={csModel}
                      onChange={(e) => setCsModel(e.target.value)}
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="cs-upc">{t("inventory:upc")}</Label>
                    <Input
                      id="cs-upc"
                      value={csUpc}
                      onChange={(e) => setCsUpc(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Label htmlFor="cs-description">{t("inventory:description")}</Label>
                    <textarea
                      id="cs-description"
                      value={csDescription}
                      onChange={(e) => setCsDescription(e.target.value)}
                      required
                      rows={2}
                      className="mt-1 w-full rounded-md border bg-background p-2 text-sm"
                    />
                  </div>
                  <div>
                    <Label htmlFor="cs-retail">{t("inventory:binManagerEstimateRetail")}</Label>
                    <Input
                      id="cs-retail"
                      type="number"
                      step="0.01"
                      min="0"
                      value={csEstimatedRetail}
                      onChange={(e) => setCsEstimatedRetail(e.target.value)}
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="cs-cost">{t("inventory:binManagerAvgCost")}</Label>
                    <Input
                      id="cs-cost"
                      type="number"
                      step="0.01"
                      min="0"
                      value={csAvgCost}
                      onChange={(e) => setCsAvgCost(e.target.value)}
                      className="mt-1"
                    />
                  </div>

                  {csResult && (
                    <p className="text-sm text-emerald-600 sm:col-span-2">
                      {t("inventory:binManagerCreated", { sku: csResult.binManagerSku })}
                      {csResult.warning ? ` ${csResult.warning}` : ""}
                    </p>
                  )}
                  {csError && <p className="text-sm text-destructive sm:col-span-2">{csError}</p>}

                  <div className="flex items-end gap-2 sm:col-span-2">
                    <Button type="submit" disabled={createBinManagerSkuMutation.isPending}>
                      {createBinManagerSkuMutation.isPending
                        ? t("inventory:saving")
                        : t("inventory:createSku")}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setCreateSkuOpen(false);
                        resetCreateSkuForm();
                      }}
                    >
                      {t("inventory:cancel")}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          {addOpen && (
            <Card>
              <CardHeader className="p-4 pb-2">
                <CardTitle className="text-base">{t("inventory:addProduct")}</CardTitle>
                <CardDescription>{t("inventory:addProductHint")}</CardDescription>
              </CardHeader>
              <CardContent className="p-4 pt-0">
                <form
                  onSubmit={handleCreate}
                  className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3"
                >
                  <div>
                    <Label htmlFor="new-sku">{t("inventory:sku")}</Label>
                    <Input
                      id="new-sku"
                      value={newSku}
                      onChange={(e) => setNewSku(e.target.value.toUpperCase())}
                      placeholder="STK-0001"
                      required
                      className="mt-1"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Label htmlFor="new-title">{t("inventory:listingTitle")}</Label>
                    <Input
                      id="new-title"
                      value={newTitle}
                      onChange={(e) => setNewTitle(e.target.value)}
                      required
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="new-unit">{t("inventory:filterByUnit")}</Label>
                    <Select value={newUnit} onValueChange={(v) => setNewUnit(v as Unit)}>
                      <SelectTrigger id="new-unit" className="mt-1 w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {INVENTORY_UNITS.map((u) => (
                          <SelectItem key={u} value={u}>
                            {t(`common:units.${u}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="new-kind">{t("inventory:kind")}</Label>
                    <Select value={newKind} onValueChange={(v) => setNewKind(v as ProductKind)}>
                      <SelectTrigger id="new-kind" className="mt-1 w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PRODUCT_KINDS.map((k) => (
                          <SelectItem key={k} value={k}>
                            {t(`inventory:productKind.${k}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="new-brand">{t("inventory:brandLabel")}</Label>
                    <Select value={newBrand} onValueChange={(v) => setNewBrand(v as Brand)}>
                      <SelectTrigger id="new-brand" className="mt-1 w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ninjamo">{t("inventory:brand.ninjamo")}</SelectItem>
                        <SelectItem value="peeker">{t("inventory:brand.peeker")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="new-list-price">{t("inventory:listPrice")}</Label>
                    <Input
                      id="new-list-price"
                      type="number"
                      step="0.01"
                      min="0"
                      value={newListPrice}
                      onChange={(e) => setNewListPrice(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="new-landed-cost">{t("inventory:landedCost")}</Label>
                    <Input
                      id="new-landed-cost"
                      type="number"
                      step="0.01"
                      min="0"
                      value={newLandedCost}
                      onChange={(e) => setNewLandedCost(e.target.value)}
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="new-qty">{t("inventory:initialQuantity")}</Label>
                    <Input
                      id="new-qty"
                      type="number"
                      min="0"
                      step="1"
                      value={newQty}
                      onChange={(e) => setNewQty(e.target.value)}
                      placeholder="0"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>{t("inventory:dimensions")}</Label>
                    <div className="mt-1 grid grid-cols-4 gap-1.5">
                      <Input
                        aria-label={t("inventory:lengthIn")}
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder={t("inventory:lengthIn")}
                        value={newLengthIn}
                        onChange={(e) => setNewLengthIn(e.target.value)}
                      />
                      <Input
                        aria-label={t("inventory:widthIn")}
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder={t("inventory:widthIn")}
                        value={newWidthIn}
                        onChange={(e) => setNewWidthIn(e.target.value)}
                      />
                      <Input
                        aria-label={t("inventory:heightIn")}
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder={t("inventory:heightIn")}
                        value={newHeightIn}
                        onChange={(e) => setNewHeightIn(e.target.value)}
                      />
                      <Input
                        aria-label={t("inventory:weightLb")}
                        type="number"
                        step="0.001"
                        min="0"
                        placeholder={t("inventory:weightLb")}
                        value={newWeightLb}
                        onChange={(e) => setNewWeightLb(e.target.value)}
                      />
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {t("inventory:dimensionsHint")}
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="new-image">{t("inventory:mainPhoto")}</Label>
                    <Input
                      ref={newImageInputRef}
                      id="new-image"
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/gif,image/avif"
                      onChange={(e) => setNewImageFile(e.target.files?.[0] ?? null)}
                      className="mt-1"
                    />
                    {newImageFile && (
                      <div className="mt-1.5 flex items-center gap-1.5">
                        <img
                          src={URL.createObjectURL(newImageFile)}
                          alt=""
                          className="h-8 w-8 rounded border object-cover"
                        />
                        <span className="truncate text-xs text-muted-foreground">
                          {newImageFile.name}
                        </span>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="h-6 px-1.5 text-xs"
                          onClick={() => {
                            setNewImageFile(null);
                            if (newImageInputRef.current) newImageInputRef.current.value = "";
                          }}
                        >
                          <X className="h-3 w-3" aria-hidden />
                          {t("inventory:removePhoto")}
                        </Button>
                      </div>
                    )}
                  </div>

                  <div className="flex items-end gap-2 sm:col-span-2 lg:col-span-3">
                    <Button type="submit" disabled={!canCreate || createProduct.isPending}>
                      {createProduct.isPending
                        ? t("inventory:saving")
                        : t("inventory:createProduct")}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setAddOpen(false);
                        resetAddProductForm();
                      }}
                    >
                      {t("inventory:cancel")}
                    </Button>
                  </div>

                  {createError && (
                    <p className="text-sm text-destructive sm:col-span-2 lg:col-span-3">
                      {createError}
                    </p>
                  )}
                </form>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <Stat
          label={t("inventory:activeSkusTotal")}
          value={integer(summary?.activeSkusTotal ?? 0, locale)}
          Icon={PackageSearch}
        />
        {STAT_MARKETPLACES.map((m) => (
          <Stat
            key={m}
            label={MARKETPLACE_LABELS[m] ?? m}
            value={integer(
              summary?.activeByMarketplace.find((c) => c.marketplace === m)?.count ?? 0,
              locale,
            )}
            Icon={() => <MarketplaceIcon marketplace={m} className="h-4 w-auto" />}
          />
        ))}
      </div>

      {crossChannelGroups.length > 0 && (
        <Card className="mb-5">
          {/* Collapsed by default — this used to render every group expanded
              and pushed the actual inventory grid off-screen. */}
          <button
            type="button"
            className="tap flex w-full items-center gap-2 p-4 text-left"
            aria-expanded={crossChannelOpen}
            onClick={() => setCrossChannelOpen((v) => !v)}
          >
            {crossChannelOpen ? (
              <ChevronDown className="h-4 w-4 shrink-0" aria-hidden />
            ) : (
              <ChevronRight className="h-4 w-4 shrink-0" aria-hidden />
            )}
            <span className="font-medium">{t("inventory:crossChannelTitle")}</span>
            <Badge variant="info">{crossChannelGroups.length}</Badge>
          </button>
          {crossChannelOpen && (
            <CardContent className="space-y-2 p-4 pt-0">
              <p className="mb-2 text-sm text-muted-foreground">
                {t("inventory:crossChannelHint")}
              </p>
              {crossChannelGroups.map((g) => (
                <div
                  key={g.channelSku}
                  className="flex flex-col gap-1.5 rounded-md border p-2.5 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div>
                    <span className="font-mono text-xs text-muted-foreground">{g.channelSku}</span>
                    <span className="ml-2">{g.title || g.channelSku}</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    {g.channels.map((c) => (
                      <Badge key={c.id} variant="outline">
                        {c.marketplace}
                      </Badge>
                    ))}
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        // The FIRST channel's row is the resolve target -- the
                        // sibling auto-resolve already built into
                        // /unmatched-skus/:id/map and /create-product picks up
                        // every other channel sharing this exact channelSku,
                        // so resolving one resolves the whole group.
                        const first = g.channels[0];
                        if (!first) return;
                        openResolve({
                          id: first.id,
                          channelSku: g.channelSku,
                          title: g.title,
                          price: first.price,
                        });
                      }}
                    >
                      {t("inventory:resolveGroup")}
                    </Button>
                  </div>
                </div>
              ))}
            </CardContent>
          )}
        </Card>
      )}

      {inboundShipments != null && inboundShipments.length > 0 && (
        <Card className="mb-5">
          <button
            type="button"
            className="tap flex w-full items-center gap-2 p-4 text-left"
            aria-expanded={inboundShipmentsOpen}
            onClick={() => setInboundShipmentsOpen((v) => !v)}
          >
            {inboundShipmentsOpen ? (
              <ChevronDown className="h-4 w-4 shrink-0" aria-hidden />
            ) : (
              <ChevronRight className="h-4 w-4 shrink-0" aria-hidden />
            )}
            <span className="font-medium">{t("inventory:inboundShipmentsTitle")}</span>
            <Badge variant="info">{inboundShipments.length}</Badge>
          </button>
          {inboundShipmentsOpen && (
            <CardContent className="space-y-2 p-4 pt-0">
              <p className="mb-2 text-sm text-muted-foreground">
                {t("inventory:inboundShipmentsHint")}
              </p>
              {inboundShipments.map((s) => (
                <div
                  key={s.shipmentId}
                  className="flex flex-col gap-1.5 rounded-md border p-2.5 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div>
                    <span className="font-mono text-xs text-muted-foreground">{s.shipmentId}</span>
                    <span className="ml-2">{s.shipmentName ?? s.shipmentId}</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    {s.destinationFulfillmentCenterId && (
                      <Badge variant="outline">{s.destinationFulfillmentCenterId}</Badge>
                    )}
                    <Badge variant="secondary">
                      {t(`inventory:inboundShipmentStatus.${s.status}`, {
                        defaultValue: s.status,
                      })}
                    </Badge>
                  </div>
                </div>
              ))}
            </CardContent>
          )}
        </Card>
      )}

      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="flex-1">
          <Label htmlFor="sku-search" className="sr-only">
            {t("inventory:search")}
          </Label>
          <Input
            id="sku-search"
            type="search"
            value={term}
            onChange={(e) => {
              setTerm(e.target.value);
              setUnmatchedPage(0);
            }}
            placeholder={t("inventory:search")}
          />
        </div>

        <fieldset className="flex flex-wrap gap-1 border-0 p-0">
          <legend className="sr-only">{t("inventory:filterByUnit")}</legend>
          {UNIT_FILTERS.map((u) => (
            <Button
              key={u}
              type="button"
              size="sm"
              variant={unit === u ? "default" : "ghost"}
              onClick={() => setUnit(u)}
            >
              {u === "all" ? t("inventory:allUnits") : t(`common:units.${u}`)}
            </Button>
          ))}
        </fieldset>
      </div>

      <div className="mb-4">
        <fieldset className="flex flex-wrap gap-1 border-0 p-0">
          <legend className="sr-only">{t("inventory:filterByChannel")}</legend>
          <Button
            type="button"
            size="sm"
            variant={channelFilter === "all" ? "default" : "ghost"}
            onClick={() => {
              setChannelFilter("all");
              setUnmatchedPage(0);
            }}
          >
            {t("inventory:allChannels")}
          </Button>
          {CHANNEL_FILTERS.map((c) => (
            <Button
              key={c}
              type="button"
              size="sm"
              variant={channelFilter === c ? "default" : "ghost"}
              title={c}
              aria-label={c}
              onClick={() => {
                setChannelFilter(c);
                setUnmatchedPage(0);
              }}
            >
              <MarketplaceIcon marketplace={c} className="!h-7 !w-auto" aria-hidden />
            </Button>
          ))}
          <Button
            type="button"
            size="sm"
            variant={channelFilter === "unlisted" ? "default" : "ghost"}
            onClick={() => {
              setChannelFilter("unlisted");
              setUnmatchedPage(0);
            }}
          >
            {t("inventory:unlistedFilter")}
          </Button>
          <Button
            type="button"
            size="sm"
            variant={channelFilter === "out_of_stock" ? "default" : "ghost"}
            onClick={() => {
              setChannelFilter("out_of_stock");
              setUnmatchedPage(0);
            }}
          >
            {t("inventory:outOfStockFilter")}
          </Button>
        </fieldset>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {channelFilter === "amazon" && (
          <div className="tap flex items-center gap-2 px-1">
            <Checkbox
              id="fba-only"
              checked={fbaOnly}
              onCheckedChange={(v) => setFbaOnly(v === true)}
            />
            <Label htmlFor="fba-only">{t("inventory:fbaOnlyFilter")}</Label>
          </div>
        )}
        {channelFilter === "amazon" && (
          <>
            <div className="tap flex items-center gap-2 px-1">
              <Checkbox
                id="brand-ninjamo"
                checked={brandFilter === "ninjamo"}
                onCheckedChange={(v) => setBrandFilter(v === true ? "ninjamo" : "all")}
              />
              <Label htmlFor="brand-ninjamo">{t("inventory:brand.ninjamo")}</Label>
            </div>
            <div className="tap flex items-center gap-2 px-1">
              <Checkbox
                id="brand-peeker"
                checked={brandFilter === "peeker"}
                onCheckedChange={(v) => setBrandFilter(v === true ? "peeker" : "all")}
              />
              <Label htmlFor="brand-peeker">{t("inventory:brand.peeker")}</Label>
            </div>
          </>
        )}
        {!isLoading && (
          <span className="text-sm text-muted-foreground">
            {t("inventory:matchingCount", { count: totalMatching })}
          </span>
        )}
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="ml-auto text-muted-foreground"
          onClick={() => setShowLicenseTools((v) => !v)}
        >
          {showLicenseTools ? (
            <ChevronDown className="h-3.5 w-3.5" aria-hidden />
          ) : (
            <ChevronRight className="h-3.5 w-3.5" aria-hidden />
          )}
          {t("inventory:licenseToolsToggle")}
        </Button>
      </div>

      {/* Isaac, 2026-09-19: "healthy, overstock, re-stock so i can see
          exactly how many of each are in what category." Counts come from
          GET /inventory/summary (one grouped query), not from counting rows
          in the current page -- same reasoning as totalMatching above. */}
      {channelFilter === "amazon" && fbaOnly && summary && (
        <fieldset className="mb-4 flex flex-wrap gap-1 border-0 p-0">
          <legend className="sr-only">{t("inventory:fbaStockHealthLegend")}</legend>
          <Button
            type="button"
            size="sm"
            variant={fbaBucket === "all" ? "default" : "ghost"}
            onClick={() => setFbaBucket("all")}
          >
            {t("inventory:fbaBucketAll")}
          </Button>
          {(["restock", "healthy", "overstock", "no_sales_data"] as const).map((bucket) => (
            <Button
              key={bucket}
              type="button"
              size="sm"
              variant={fbaBucket === bucket ? "default" : "ghost"}
              onClick={() => setFbaBucket(bucket)}
            >
              {t(`inventory:fbaStockHealth.${bucket}`)} (
              {integer(summary.fbaStockHealthCounts[bucket], locale)})
            </Button>
          ))}
        </fieldset>
      )}

      {/* Isaac, 2026-09-23: "see exactly what is out of stock on each
          platform... with the corresponding number next to the option."
          Same pattern as the FBA stock-health pills above -- counts come
          from the summary query, not the current page. */}
      {channelFilter === "out_of_stock" && summary && (
        <fieldset className="mb-4 flex flex-wrap gap-1 border-0 p-0">
          <legend className="sr-only">{t("inventory:outOfStockByMarketplaceLegend")}</legend>
          <Button
            type="button"
            size="sm"
            variant={outOfStockMarketplace === "all" ? "default" : "ghost"}
            onClick={() => setOutOfStockMarketplace("all")}
          >
            {t("inventory:fbaBucketAll")}
          </Button>
          {summary.outOfStockByMarketplace.map(({ marketplace, count }) => (
            <Button
              key={marketplace}
              type="button"
              size="sm"
              variant={outOfStockMarketplace === marketplace ? "default" : "ghost"}
              onClick={() => setOutOfStockMarketplace(marketplace)}
            >
              <MarketplaceIcon marketplace={marketplace} className="!h-4 !w-auto" aria-hidden />(
              {integer(count, locale)})
            </Button>
          ))}
        </fieldset>
      )}

      {showLicenseTools && (
        <div className="mb-4 flex flex-wrap items-center gap-2 rounded-md border border-dashed p-2.5">
          <div className="tap flex items-center gap-2 px-1">
            <Checkbox
              id="unreviewed-only"
              checked={unreviewedOnly}
              onCheckedChange={(v) => setUnreviewedOnly(v === true)}
            />
            <Label htmlFor="unreviewed-only">{t("inventory:unreviewedOnly")}</Label>
          </div>
          {rows.length > 0 && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="ml-auto"
              onClick={() => {
                setLicenseBulkError(null);
                setLicenseBulkResult(null);
                setLicenseBulkOpen(true);
              }}
            >
              <ShieldAlert className="h-3.5 w-3.5" aria-hidden />
              {t("inventory:licenseBulkButton")}
            </Button>
          )}
        </div>
      )}

      {licenseBulkResult && (
        <p className="mb-3 rounded-md border border-[hsl(var(--success)/0.4)] bg-[hsl(var(--success)/0.1)] p-3 text-sm">
          {licenseBulkResult}
        </p>
      )}

      {isLoading && <p className="text-muted-foreground">{t("common:loading")}</p>}

      {!isLoading && rows.length === 0 && unmatchedRows.length === 0 && (
        <p className="rounded-lg border border-dashed p-8 text-center text-muted-foreground">
          {t("inventory:noMatches")}
        </p>
      )}

      {statusError && <p className="mb-3 text-sm text-destructive">{statusError}</p>}

      {bulkResult && (
        <p className="mb-3 rounded-md border border-[hsl(var(--success)/0.4)] bg-[hsl(var(--success)/0.1)] p-3 text-sm">
          {bulkResult}
        </p>
      )}

      {/* Bulk action bar — appears only when listings are selected. This is
          the flow that actually closes a 4,000-row backlog; one-at-a-time
          matching never could. */}
      {selectedUnmatched.size > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-md border bg-card p-3">
          <span className="text-sm font-medium">
            {t("inventory:selectedCount", { count: selectedUnmatched.size })}
          </span>
          <span className="ml-auto inline-flex gap-1.5">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setSelectedUnmatched(new Set())}
            >
              {t("inventory:clearSelection")}
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={() => {
                setBulkResult(null);
                setSkuDrafts({});
                setSkuPrefix("");
                setReviewOpen(true);
              }}
            >
              {t("inventory:reviewAndCreate", { count: selectedUnmatched.size })}
            </Button>
          </span>
        </div>
      )}

      {showUnmatched && unmatchedRows.length > 0 && selectedUnmatched.size === 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setSelectedUnmatched(new Set(unmatchedRows.map((u) => u.id)))}
          >
            {t("inventory:selectAllOnPage", { count: unmatchedRows.length })}
          </Button>
          <span className="text-sm text-muted-foreground">{t("inventory:bulkCreateHint")}</span>
        </div>
      )}

      {(rows.length > 0 || unmatchedRows.length > 0) && (
        <Card>
          {/* No forced min-width here (Isaac, 2026-09-23: "I want to be able
              to see everything without the slider") -- the old 76rem
              (1216px) floor guaranteed a horizontal scrollbar on any screen
              narrower than that plus the page's own side padding/nav, which
              is most normal laptop screens. `Table` already wraps in its own
              overflow-x-auto as a safety net for a genuinely narrow window;
              letting columns size to real content (Product already wraps)
              is what actually removes the slider on an ordinary desktop. */}
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8" />
                <TableHead>{t("inventory:ninjamoSku")}</TableHead>
                {showAsin && <TableHead>{t("inventory:asin")}</TableHead>}
                <TableHead>{t("inventory:product")}</TableHead>
                <TableHead>{t("inventory:channels")}</TableHead>
                <TableHead className="text-right">{t("inventory:onHand")}</TableHead>
                <TableHead className="text-right">{t("inventory:reserved")}</TableHead>
                <TableHead className="text-right">{t("inventory:sellable")}</TableHead>
                <TableHead className="text-right">{t("inventory:runRate")}</TableHead>
                <TableHead className="text-right">{t("inventory:lastSold")}</TableHead>
                <TableHead className="text-right" title={t("inventory:marginHint")}>
                  {t("inventory:margin")}
                </TableHead>
                {/* Isaac, 2026-09-24: "that column should only apply when
                    dealing all things with amazon, not walmart, not eBay,
                    nor shopify" -- gating just the cell (dash on every other
                    channel) still left the header itself sitting there for
                    every other channel, still Amazon-only noise. Dropping
                    the whole column, not just its content, when the filter
                    isn't Amazon. */}
                {channelFilter === "amazon" && (
                  <TableHead className="text-right" title={t("inventory:fbaStockHint")}>
                    {t("inventory:fbaStock")}
                  </TableHead>
                )}
                <TableHead className="text-right">{t("inventory:landedCost")}</TableHead>
                <TableHead className="text-right">{t("inventory:listPrice")}</TableHead>
                <TableHead className="text-right">{t("inventory:actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => {
                const archived = r.status === "archived" || r.status === "discontinued";
                const expanded = expandedSku === r.sku;
                return (
                  <Fragment key={r.id}>
                    <TableRow>
                      <TableCell className="w-8 p-0 text-center">
                        {r.channels.length > 0 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            aria-label={t("inventory:expandChannels")}
                            onClick={() => setExpandedSku(expanded ? null : r.sku)}
                          >
                            {expanded ? (
                              <ChevronDown className="h-4 w-4" aria-hidden />
                            ) : (
                              <ChevronRight className="h-4 w-4" aria-hidden />
                            )}
                          </Button>
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-xs">{r.sku}</TableCell>
                      {showAsin && (
                        <TableCell className="font-mono text-xs text-muted-foreground">
                          {r.amazonAsin ?? "–"}
                        </TableCell>
                      )}
                      <TableCell>
                        <span className="flex items-start gap-2">
                          {/* One thumbnail, the product's own main photo --
                              already just a URL from Amazon/eBay's CDN
                              (products.images[0], MAIN-variant-first per
                              fetchContent's ordering), not anything this app
                              stores or processes itself. Lazy-loaded since a
                              full page can be up to 200 rows. */}
                          {r.images[0] ? (
                            <img
                              src={r.images[0]}
                              alt=""
                              loading="lazy"
                              className="h-10 w-10 shrink-0 rounded border object-cover"
                            />
                          ) : (
                            <span
                              className="flex h-10 w-10 shrink-0 items-center justify-center rounded border bg-muted text-muted-foreground"
                              aria-hidden
                            >
                              <ImageOff className="h-4 w-4" />
                            </span>
                          )}
                          <span>
                            <span className="block">{r.title}</span>
                            <span className="mt-1 flex flex-wrap items-center gap-1.5">
                              <Badge variant="secondary">
                                {t(`common:units.${r.businessUnit}`)}
                              </Badge>
                              {r.franchise && <Badge variant="outline">{r.franchise}</Badge>}
                              {/* "Original" is the common, no-action-needed case
                                  (Isaac, 2026-09-19: licence shouldn't dominate
                                  this page) -- only surface the badge when
                                  there's actually something to look at. */}
                              {r.licenseKind !== "original" && (
                                <Badge variant={LICENSE_VARIANT[r.licenseKind]}>
                                  {t(`inventory:license.${r.licenseKind}`)}
                                </Badge>
                              )}
                              {r.status !== "active" && (
                                <Badge variant="outline">
                                  {t(`inventory:productStatus.${r.status}`)}
                                </Badge>
                              )}
                            </span>
                          </span>
                        </span>
                      </TableCell>
                      <TableCell>
                        {r.channels.length > 0 ? (
                          <span className="flex flex-wrap gap-1">
                            {r.channels.map((c) => (
                              <Badge key={c} variant="outline">
                                {c}
                              </Badge>
                            ))}
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">
                            {t("inventory:notListed")}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {r.stockMode === "made_to_order" ? (
                          <span title={t("inventory:madeToOrderHint")}>∞</span>
                        ) : (
                          integer(r.onHand, locale)
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-muted-foreground">
                        {integer(r.reserved, locale)}
                      </TableCell>
                      <TableCell className="text-right font-medium tabular-nums">
                        {r.stockMode === "made_to_order" ? "∞" : integer(r.sellable, locale)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-muted-foreground">
                        {r.unitsPerDay > 0
                          ? t("inventory:unitsPerDay", { count: r.unitsPerDay })
                          : "—"}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-muted-foreground">
                        {r.lastSoldAt ? dateOnly(r.lastSoldAt, locale) : t("inventory:neverSold")}
                      </TableCell>
                      <TableCell
                        className={cn(
                          "text-right tabular-nums",
                          r.grossMargin30d != null && r.grossMargin30d < 0 && "text-destructive",
                        )}
                        title={t("inventory:marginHint")}
                      >
                        {r.grossMargin30d == null
                          ? "—"
                          : money(String(r.grossMargin30d), "USD", locale)}
                      </TableCell>
                      {/* Isaac, 2026-09-23/24: "why is it showing amazon
                          restock, overstock etc? ... only when i am
                          filtering my app for amazon listings" -- gating just
                          the cell's content (a dash on every other channel)
                          still left the column itself, header included,
                          sitting there as Amazon-only noise on every other
                          channel filter. Dropping the whole cell, matching
                          the header above, rather than rendering a dash. */}
                      {channelFilter === "amazon" && (
                        <TableCell className="text-right">
                          {r.fbaStockHealth == null ? (
                            <span className="text-muted-foreground">—</span>
                          ) : (
                            <>
                              <Badge
                                variant={FBA_STOCK_VARIANT[r.fbaStockHealth]}
                                className="gap-1"
                                title={
                                  r.fbaHasUnfulfillable
                                    ? t("inventory:fbaUnfulfillableHint")
                                    : undefined
                                }
                              >
                                {r.fbaStockHealth === "healthy" && (
                                  <Check className="h-3 w-3" aria-hidden />
                                )}
                                {t(`inventory:fbaStockHealth.${r.fbaStockHealth}`)}
                              </Badge>
                              {/* Isaac, 2026-09-19: "i want to see the actual
                                  stock ready to sell" -- fulfillable, not
                                  inbound (that's still in transit, not
                                  sellable yet). Shown separately below since it
                                  answers a different question than the bucket. */}
                              <span className="mt-0.5 block text-xs text-muted-foreground">
                                {t("inventory:fbaFulfillableCount", {
                                  count: r.fbaFulfillableQuantity ?? 0,
                                })}
                                {r.fbaInboundQuantity != null && r.fbaInboundQuantity > 0
                                  ? ` (+${integer(r.fbaInboundQuantity, locale)} ${t("inventory:fbaInboundShort")})`
                                  : ""}
                              </span>
                            </>
                          )}
                          {/* Amazon's OWN restock recommendation -- a separate
                              signal from the bucket above (our own run-rate
                              calc), which may agree or disagree. A deliberately
                              separate badge, not stacked onto the bucket badge
                              as an icon (Isaac had that pattern removed once
                              already for reading as contradictory next to a
                              healthy status). */}
                          {r.restockFlagId && (
                            <div className="mt-1 flex items-center justify-end gap-1">
                              <Badge variant="warning" className="gap-1">
                                {t("inventory:amazonRestockFlag")}
                              </Badge>
                              <button
                                type="button"
                                className="text-muted-foreground hover:text-foreground"
                                title={t("inventory:acknowledgeFlag")}
                                onClick={() =>
                                  resolveRestockFlag.mutate({
                                    id: r.restockFlagId as string,
                                    action: "acknowledge",
                                  })
                                }
                              >
                                <Check className="h-3.5 w-3.5" aria-hidden />
                              </button>
                              <button
                                type="button"
                                className="text-muted-foreground hover:text-foreground"
                                title={t("inventory:dismissFlag")}
                                onClick={() =>
                                  resolveRestockFlag.mutate({
                                    id: r.restockFlagId as string,
                                    action: "dismiss",
                                  })
                                }
                              >
                                <X className="h-3.5 w-3.5" aria-hidden />
                              </button>
                            </div>
                          )}
                        </TableCell>
                      )}
                      <TableCell className="text-right tabular-nums">
                        {money(r.landedCost, "USD", locale)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {money(r.listPrice, "USD", locale)}
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="inline-flex gap-1.5">
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setListError(null);
                              setListingProduct({ id: r.id, sku: r.sku });
                            }}
                          >
                            <Store className="h-3.5 w-3.5" aria-hidden />
                            {t("inventory:listOnChannel")}
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => setLinkingProduct({ id: r.id, sku: r.sku })}
                          >
                            <Link2 className="h-3.5 w-3.5" aria-hidden />
                            {t("inventory:linkListing")}
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => openEditProduct(r)}
                          >
                            <Pencil className="h-3.5 w-3.5" aria-hidden />
                            {t("inventory:edit")}
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => openAdjust(r.sku)}
                          >
                            <SlidersHorizontal className="h-3.5 w-3.5" aria-hidden />
                            {t("inventory:adjust")}
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            disabled={setProductStatus.isPending}
                            onClick={() => {
                              setStatusError(null);
                              setProductStatus.mutate({
                                sku: r.sku,
                                status: archived ? "active" : "archived",
                              });
                            }}
                          >
                            {archived ? (
                              <ArchiveRestore className="h-3.5 w-3.5" aria-hidden />
                            ) : (
                              <Archive className="h-3.5 w-3.5" aria-hidden />
                            )}
                            {archived ? t("inventory:restore") : t("inventory:archive")}
                          </Button>
                        </span>
                      </TableCell>
                    </TableRow>
                    {expanded && (
                      <TableRow className="bg-muted/20 hover:bg-muted/20">
                        <TableCell
                          colSpan={13 + (channelFilter === "amazon" ? 1 : 0) + (showAsin ? 1 : 0)}
                          className="p-4"
                        >
                          <ChannelBreakdown
                            detail={detail}
                            runRate={runRate}
                            profit={profit}
                            allocationSum={allocationSum}
                            locale={locale}
                            t={t}
                            onEdit={openEdit}
                            onUnlink={(id) => unlinkListing.mutate(id)}
                            unlinking={unlinkListing.isPending}
                          />
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
              {unmatchedRows.map((u) => (
                <TableRow key={u.id} className="bg-muted/30">
                  <TableCell className="w-8 p-0 text-center">
                    <Checkbox
                      checked={selectedUnmatched.has(u.id)}
                      onCheckedChange={() => toggleUnmatchedSelection(u.id)}
                      aria-label={t("inventory:selectListing", { sku: u.channelSku })}
                    />
                  </TableCell>
                  {/* An unmatched row has NO Ninjamo SKU yet — that's the
                      whole reason it's unmatched. Showing its marketplace SKU
                      under a column headed "Ninjamo SKU" is exactly the
                      conflation Isaac flagged, so say what it actually is. */}
                  <TableCell className="text-xs text-muted-foreground">
                    {t("inventory:noNinjamoSku")}
                  </TableCell>
                  {showAsin && (
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {u.marketplace === "amazon" && u.marketplaceId ? u.marketplaceId : "–"}
                    </TableCell>
                  )}
                  <TableCell>
                    <span className="block">{u.title || u.channelSku}</span>
                    <span className="mt-1 flex flex-col gap-0.5 text-xs text-muted-foreground">
                      <span>
                        {t("inventory:marketplaceSkuLabel")}{" "}
                        <span className="font-mono">{u.channelSku}</span>
                      </span>
                      {u.marketplaceId && (
                        <span>
                          {t("inventory:marketplaceIdLabel")}{" "}
                          <span className="font-mono">{u.marketplaceId}</span>
                        </span>
                      )}
                    </span>
                    <span className="mt-1 flex flex-wrap items-center gap-1.5">
                      <Badge variant="warning">{t("inventory:unmatched")}</Badge>
                      {u.crossChannelCount > 0 && (
                        <Badge variant="info">
                          {t("inventory:alsoOnOtherChannels", { count: u.crossChannelCount })}
                        </Badge>
                      )}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{u.marketplace}</Badge>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {u.publishedQuantity != null ? integer(u.publishedQuantity, locale) : "—"}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">—</TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">—</TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">—</TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">—</TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">—</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {u.price ? money(u.price, "USD", locale) : "—"}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        openResolve({
                          id: u.id,
                          channelSku: u.channelSku,
                          title: u.title,
                          price: u.price,
                        })
                      }
                    >
                      {t("inventory:matchListing")}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {showUnmatched && unmatchedTotal > UNMATCHED_PAGE_SIZE && (
            <div className="flex items-center justify-between border-t p-3 text-sm text-muted-foreground">
              <span>
                {t("inventory:unmatchedPageInfo", {
                  shown: Math.min(
                    unmatchedPage * UNMATCHED_PAGE_SIZE + unmatchedRows.length,
                    unmatchedTotal,
                  ),
                  total: unmatchedTotal,
                })}
              </span>
              <span className="inline-flex gap-1.5">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={unmatchedPage === 0}
                  onClick={() => setUnmatchedPage((p) => Math.max(0, p - 1))}
                >
                  {t("inventory:previousPage")}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={(unmatchedPage + 1) * UNMATCHED_PAGE_SIZE >= unmatchedTotal}
                  onClick={() => setUnmatchedPage((p) => p + 1)}
                >
                  {t("inventory:nextPage")}
                </Button>
              </span>
            </div>
          )}
        </Card>
      )}

      <p className="mt-4 text-sm text-muted-foreground">{t("inventory:sellableNote")}</p>

      <Dialog
        open={editingChannel !== null}
        onOpenChange={(open) => !open && setEditingChannel(null)}
      >
        <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto">
          {editingChannel && (
            <>
              <DialogHeader>
                <DialogTitle>
                  {expandedSku} · {editingChannel.marketplace}
                </DialogTitle>
                <DialogDescription>{editingChannel.accountLabel}</DialogDescription>
              </DialogHeader>

              {PUSHABLE_MARKETPLACES.includes(editingChannel.marketplace) &&
              editingChannel.marketplace === "amazon" &&
              !amazonPushSupported(detail?.product.businessUnit ?? "stickers") ? (
                <p className="rounded-md border border-[hsl(var(--chart-3)/0.45)] bg-[hsl(var(--chart-3)/0.1)] p-3 text-sm text-muted-foreground">
                  {t("inventory:amazonPushUnsupportedProductType")}
                </p>
              ) : PUSHABLE_MARKETPLACES.includes(editingChannel.marketplace) ? (
                <div className="rounded-md border border-[hsl(var(--chart-3)/0.45)] bg-[hsl(var(--chart-3)/0.1)] p-3 text-sm">
                  {editingChannel.externalId ? (
                    <p>
                      {t("inventory:linkedListing", {
                        channel: editingChannel.marketplace,
                        status: t(`inventory:listingStatus.${editingChannel.status}`),
                      })}
                    </p>
                  ) : (
                    <p>{t("inventory:notPushedLive", { channel: editingChannel.marketplace })}</p>
                  )}
                  {pushSuccessMessage && (
                    <p className="mt-1 font-medium text-[hsl(var(--success))]">
                      {pushSuccessMessage}
                    </p>
                  )}
                  {pushError && <p className="mt-1 text-destructive">{pushError}</p>}
                  {pushWarning && (
                    <p className="mt-1 text-xs text-[hsl(var(--chart-3))]">{pushWarning}</p>
                  )}
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={pushListing.isPending}
                      onClick={() => {
                        setPushError(null);
                        setPushSuccessMessage(null);
                        setPushWarning(null);
                        pushListing.mutate({ status: "draft" });
                      }}
                    >
                      {editingChannel.externalId
                        ? t("inventory:pushUpdateDraft")
                        : t("inventory:pushDraft")}
                    </Button>
                    {!confirmPublish ? (
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => {
                          setPushError(null);
                          setPushSuccessMessage(null);
                          setConfirmPublish(true);
                        }}
                      >
                        {t("inventory:pushLive")}
                      </Button>
                    ) : (
                      <span className="inline-flex flex-wrap items-center gap-1.5">
                        <span className="text-xs">{t("inventory:pushLiveConfirm")}</span>
                        <Button
                          type="button"
                          size="sm"
                          disabled={pushListing.isPending}
                          onClick={() => pushListing.mutate({ status: "active" })}
                        >
                          {t("inventory:pushLiveYes")}
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => setConfirmPublish(false)}
                        >
                          {t("inventory:cancel")}
                        </Button>
                      </span>
                    )}
                  </div>

                  {editingChannel.externalId &&
                    REMOTE_DELETABLE_MARKETPLACES.includes(editingChannel.marketplace) && (
                      <div className="mt-3 border-t border-[hsl(var(--chart-3)/0.3)] pt-3">
                        {deleteRemoteError && (
                          <p className="mb-1.5 text-destructive">{deleteRemoteError}</p>
                        )}
                        {!confirmDeleteRemote ? (
                          <Button
                            type="button"
                            size="sm"
                            variant="destructive"
                            onClick={() => {
                              setDeleteRemoteError(null);
                              setConfirmDeleteRemote(true);
                            }}
                          >
                            {t("inventory:deleteRemote", { channel: editingChannel.marketplace })}
                          </Button>
                        ) : (
                          <span className="inline-flex flex-wrap items-center gap-1.5">
                            <span className="text-xs">
                              {t("inventory:deleteRemoteConfirm", {
                                channel: editingChannel.marketplace,
                              })}
                            </span>
                            <Button
                              type="button"
                              size="sm"
                              variant="destructive"
                              disabled={deleteRemoteListing.isPending}
                              onClick={() => deleteRemoteListing.mutate()}
                            >
                              {t("inventory:deleteRemoteYes")}
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              onClick={() => setConfirmDeleteRemote(false)}
                            >
                              {t("inventory:cancel")}
                            </Button>
                          </span>
                        )}
                      </div>
                    )}
                </div>
              ) : (
                <p className="rounded-md border border-[hsl(var(--chart-3)/0.45)] bg-[hsl(var(--chart-3)/0.1)] p-3 text-sm">
                  {t("inventory:notPushedLive", { channel: editingChannel.marketplace })}
                </p>
              )}

              <div className="space-y-4">
                <div>
                  <h4 className="mb-2 text-sm font-medium">{t("inventory:pricingSection")}</h4>
                  <Label htmlFor="edit-price">{t("inventory:listPrice")}</Label>
                  <Input
                    id="edit-price"
                    type="number"
                    step="0.01"
                    min="0"
                    value={editPrice}
                    onChange={(e) => setEditPrice(e.target.value)}
                    className="mt-1"
                  />
                </div>

                <div>
                  <h4 className="mb-2 text-sm font-medium">{t("inventory:allocationSection")}</h4>
                  <span className="mb-2 inline-flex gap-1">
                    <Button
                      type="button"
                      size="sm"
                      variant={editMode === "manual" ? "default" : "outline"}
                      onClick={() => setEditMode("manual")}
                    >
                      {t("inventory:manualQuantity")}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant={editMode === "allocation" ? "default" : "outline"}
                      onClick={() => setEditMode("allocation")}
                    >
                      {t("inventory:allocationPercent")}
                    </Button>
                  </span>
                  {editMode === "manual" ? (
                    <Input
                      type="number"
                      min="0"
                      step="1"
                      value={editQty}
                      onChange={(e) => setEditQty(e.target.value)}
                      placeholder={t("inventory:quantity")}
                    />
                  ) : (
                    <div>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        value={editAllocation}
                        onChange={(e) => setEditAllocation(e.target.value)}
                        placeholder="50"
                      />
                      <p className="mt-1 text-xs text-muted-foreground">
                        {t("inventory:allocationPreview", {
                          qty: Math.floor(
                            (editingChannel.sellable * (Number(editAllocation) || 0)) / 100,
                          ),
                        })}
                      </p>
                      {allocationSum > 100 && (
                        <p className="mt-1 text-xs text-[hsl(var(--chart-3))]">
                          {t("inventory:allocationOverWarning", { sum: Math.round(allocationSum) })}
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {PUSHABLE_MARKETPLACES.includes(editingChannel.marketplace) && (
                  <div>
                    <h4 className="mb-2 text-sm font-medium">{t("inventory:imagesSection")}</h4>
                    {detail && detail.product.images.length > 0 ? (
                      <div className="mb-2 flex flex-wrap gap-1.5">
                        {detail.product.images.slice(0, 6).map((src) => (
                          <img
                            key={src}
                            src={src}
                            alt=""
                            className="h-14 w-14 rounded border object-cover"
                          />
                        ))}
                      </div>
                    ) : (
                      <p className="mb-2 text-xs text-muted-foreground">
                        {t("inventory:noImagesYet")}
                      </p>
                    )}
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={fetchImages.isPending}
                      onClick={() => {
                        setImagesError(null);
                        setImagesSuccess(null);
                        fetchImages.mutate();
                      }}
                    >
                      {fetchImages.isPending
                        ? t("inventory:imagesFetching")
                        : t("inventory:imagesFetchButton")}
                    </Button>
                    <input
                      id="edit-image-upload"
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/gif,image/avif"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        e.target.value = "";
                        if (!file) return;
                        setImagesError(null);
                        setImagesSuccess(null);
                        uploadExistingImage.mutate(file);
                      }}
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="ml-2"
                      disabled={uploadExistingImage.isPending}
                      onClick={() => document.getElementById("edit-image-upload")?.click()}
                    >
                      {uploadExistingImage.isPending
                        ? t("inventory:imageUploading")
                        : t("inventory:imageUploadButton")}
                    </Button>
                    {imagesSuccess && (
                      <p className="mt-1 text-xs text-[hsl(var(--success))]">{imagesSuccess}</p>
                    )}
                    {imagesError && <p className="mt-1 text-xs text-destructive">{imagesError}</p>}
                  </div>
                )}

                <div>
                  <h4 className="mb-2 text-sm font-medium">{t("inventory:contentSection")}</h4>
                  <div className="space-y-2">
                    <div>
                      <Label htmlFor="edit-title">
                        {t("inventory:contentTitle")}{" "}
                        <span className="text-xs text-muted-foreground">
                          (
                          {editingChannel.hasContentOverride
                            ? t("inventory:overridden")
                            : t("inventory:usingDefault")}
                          )
                        </span>
                      </Label>
                      <Input
                        id="edit-title"
                        value={editTitle}
                        onChange={(e) => setEditTitle(e.target.value)}
                        placeholder={contentOverride?.effective.title ?? ""}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit-bullets">{t("inventory:contentBullets")}</Label>
                      <textarea
                        id="edit-bullets"
                        value={editBullets}
                        onChange={(e) => setEditBullets(e.target.value)}
                        placeholder={t("inventory:contentBulletsHint")}
                        rows={4}
                        className="mt-1 w-full rounded-md border bg-background px-3 py-2 text-sm"
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit-description">{t("inventory:contentDescription")}</Label>
                      <textarea
                        id="edit-description"
                        value={editDescription}
                        onChange={(e) => setEditDescription(e.target.value)}
                        placeholder={contentOverride?.effective.description ?? ""}
                        rows={4}
                        className="mt-1 w-full rounded-md border bg-background px-3 py-2 text-sm"
                      />
                    </div>
                    {editingChannel.marketplace === "amazon" && (
                      <div>
                        <Label htmlFor="edit-subject-character">
                          {t("inventory:contentSubjectCharacter")}
                        </Label>
                        <Input
                          id="edit-subject-character"
                          value={editSubjectCharacter}
                          onChange={(e) => {
                            setEditSubjectCharacter(e.target.value);
                            setSubjectCharacterTouched(true);
                          }}
                          className="mt-1"
                        />
                        <p className="mt-1 text-xs text-muted-foreground">
                          {t("inventory:contentSubjectCharacterHint")}
                        </p>
                      </div>
                    )}
                    {editingChannel.marketplace === "amazon" && (
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <div>
                          <Label htmlFor="edit-color">{t("inventory:contentColor")}</Label>
                          <Input
                            id="edit-color"
                            value={editColor}
                            onChange={(e) => setEditColor(e.target.value)}
                            placeholder="Multicolor"
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label htmlFor="edit-theme">{t("inventory:contentTheme")}</Label>
                          <Input
                            id="edit-theme"
                            value={editTheme}
                            onChange={(e) => setEditTheme(e.target.value)}
                            placeholder="Pop Culture"
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label htmlFor="edit-special-features">
                            {t("inventory:contentSpecialFeatures")}
                          </Label>
                          <Input
                            id="edit-special-features"
                            value={editSpecialFeatures}
                            onChange={(e) => setEditSpecialFeatures(e.target.value)}
                            placeholder="Waterproof, UV Resistant, Removable"
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label htmlFor="edit-generic-keywords">
                            {t("inventory:contentGenericKeywords")}
                          </Label>
                          <Input
                            id="edit-generic-keywords"
                            value={editGenericKeywords}
                            onChange={(e) => setEditGenericKeywords(e.target.value)}
                            placeholder={t("inventory:contentGenericKeywordsPlaceholder")}
                            className="mt-1"
                          />
                        </div>
                        <p className="text-xs text-muted-foreground sm:col-span-2">
                          {t("inventory:contentAmazonDefaultsHint")}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {editError && <p className="text-sm text-destructive">{editError}</p>}

                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setEditingChannel(null)}>
                    {t("inventory:cancel")}
                  </Button>
                  <Button
                    type="button"
                    onClick={() => void handleSaveListing()}
                    disabled={saveListing.isPending || saveContent.isPending}
                  >
                    {saveListing.isPending || saveContent.isPending
                      ? t("inventory:saving")
                      : t("inventory:saveListing")}
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit / rename / delete a Ninjamo SKU. Renaming is safe: everything
          attaches to the stable internal id shown at the bottom, not to the
          SKU text, so channels stay linked through a rename. */}
      <Dialog
        open={editingProduct !== null}
        onOpenChange={(open) => !open && setEditingProduct(null)}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t("inventory:editSkuTitle")}</DialogTitle>
            <DialogDescription>{t("inventory:editSkuHint")}</DialogDescription>
          </DialogHeader>
          {editingProduct && (
            <div className="space-y-3">
              <div>
                <Label htmlFor="edit-sku-field">{t("inventory:ninjamoSku")}</Label>
                <Input
                  id="edit-sku-field"
                  value={editSku}
                  onChange={(e) => setEditSku(e.target.value.toUpperCase())}
                  className="mt-1 font-mono"
                />
              </div>
              <div>
                <Label htmlFor="edit-product-title">{t("inventory:product")}</Label>
                <Input
                  id="edit-product-title"
                  value={editProductTitle}
                  onChange={(e) => setEditProductTitle(e.target.value)}
                  className="mt-1"
                />
              </div>

              <div>
                <Label>{t("inventory:stockModeLabel")}</Label>
                <span className="mt-1 flex gap-1">
                  <Button
                    type="button"
                    size="sm"
                    variant={editStockMode === "tracked" ? "default" : "outline"}
                    onClick={() => setEditStockMode("tracked")}
                  >
                    {t("inventory:stockTracked")}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={editStockMode === "made_to_order" ? "default" : "outline"}
                    onClick={() => setEditStockMode("made_to_order")}
                  >
                    {t("inventory:stockMadeToOrder")}
                  </Button>
                </span>
              </div>

              {editStockMode === "tracked" ? (
                <div>
                  <Label htmlFor="edit-onhand">{t("inventory:warehouseQty")}</Label>
                  <div className="mt-1 flex gap-2">
                    <Input
                      id="edit-onhand"
                      type="number"
                      min="0"
                      step="1"
                      value={editOnHand}
                      onChange={(e) => setEditOnHand(e.target.value)}
                      className="tabular-nums"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      disabled={setStock.isPending || editOnHand.trim() === ""}
                      onClick={() =>
                        setStock.mutate({
                          sku: editingProduct.id,
                          onHand: Math.max(0, Math.trunc(Number(editOnHand))),
                        })
                      }
                    >
                      {setStock.isPending ? t("inventory:saving") : t("inventory:setQty")}
                    </Button>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t("inventory:warehouseQtyHint")}
                  </p>
                </div>
              ) : (
                <div>
                  <Label htmlFor="edit-mto-qty">{t("inventory:madeToOrderQty")}</Label>
                  <Input
                    id="edit-mto-qty"
                    type="number"
                    min="0"
                    step="1"
                    value={editMtoQty}
                    onChange={(e) => setEditMtoQty(e.target.value)}
                    className="mt-1 tabular-nums"
                  />
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t("inventory:madeToOrderHint")}
                  </p>
                </div>
              )}

              {editingProduct.channels.length > 0 && (
                <p className="rounded-md border p-2.5 text-xs text-muted-foreground">
                  {t("inventory:renameKeepsLinks", {
                    count: editingProduct.channels.length,
                    channels: editingProduct.channels.join(", "),
                  })}
                </p>
              )}

              <p className="text-xs text-muted-foreground">
                {t("inventory:internalId")} <span className="font-mono">{editingProduct.id}</span>
              </p>

              {editProductError && <p className="text-sm text-destructive">{editProductError}</p>}

              <div className="flex flex-wrap justify-between gap-2">
                {!confirmDelete ? (
                  <Button
                    type="button"
                    variant="outline"
                    className="text-destructive"
                    onClick={() => {
                      setEditProductError(null);
                      setConfirmDelete(true);
                    }}
                  >
                    <Trash2 className="h-3.5 w-3.5" aria-hidden />
                    {t("inventory:deleteSku")}
                  </Button>
                ) : (
                  <span className="inline-flex flex-wrap items-center gap-1.5">
                    <span className="text-xs text-destructive">{t("inventory:deleteConfirm")}</span>
                    <Button
                      type="button"
                      size="sm"
                      variant="destructive"
                      disabled={deleteProduct.isPending}
                      onClick={() => deleteProduct.mutate({ id: editingProduct.id, force: true })}
                    >
                      {t("inventory:deleteYes")}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => setConfirmDelete(false)}
                    >
                      {t("inventory:cancel")}
                    </Button>
                  </span>
                )}
                <span className="inline-flex gap-2">
                  <Button type="button" variant="outline" onClick={() => setEditingProduct(null)}>
                    {t("inventory:cancel")}
                  </Button>
                  <Button
                    type="button"
                    disabled={saveProduct.isPending || editSku.trim().length < 2}
                    onClick={() =>
                      saveProduct.mutate({
                        id: editingProduct.id,
                        body: {
                          sku: editSku.trim(),
                          title: editProductTitle.trim(),
                          stockMode: editStockMode,
                          madeToOrderQuantity: Math.max(0, Math.trunc(Number(editMtoQty) || 0)),
                        },
                      })
                    }
                  >
                    {saveProduct.isPending ? t("inventory:saving") : t("inventory:saveListing")}
                  </Button>
                </span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk SKU review — every suggested Ninjamo SKU is editable here
          before anything is written, with collisions surfaced up front. */}
      <Dialog open={reviewOpen} onOpenChange={(open) => !open && setReviewOpen(false)}>
        <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t("inventory:reviewTitle", { count: reviewRows.length })}</DialogTitle>
            <DialogDescription>{t("inventory:reviewHint")}</DialogDescription>
          </DialogHeader>

          <div className="flex flex-wrap items-end gap-2">
            <div>
              <Label htmlFor="sku-prefix">{t("inventory:skuPrefix")}</Label>
              <Input
                id="sku-prefix"
                value={skuPrefix}
                onChange={(e) => setSkuPrefix(e.target.value.toUpperCase())}
                placeholder="NJ-"
                className="mt-1 w-40"
              />
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setSkuDrafts({})}
              title={t("inventory:resetToSuggestions")}
            >
              {t("inventory:resetToSuggestions")}
            </Button>
            <span className="text-xs text-muted-foreground">{t("inventory:skuPrefixHint")}</span>
          </div>

          <div className="space-y-1.5">
            {reviewRows.map((u) => {
              const sku = effectiveSku(u);
              const collides = existingSkus.has(sku);
              const duped = (dupCounts[sku] ?? 0) > 1;
              const tooShort = sku.length < 2;
              const bad = collides || duped || tooShort;
              return (
                <div
                  key={u.id}
                  className="grid grid-cols-1 gap-2 rounded-md border p-2.5 sm:grid-cols-2"
                >
                  <div className="min-w-0">
                    <span className="block truncate text-sm">{u.title || u.channelSku}</span>
                    <span className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                      <Badge variant="outline">{u.marketplace}</Badge>
                      <span>
                        {t("inventory:marketplaceSkuLabel")}{" "}
                        <span className="font-mono">{u.channelSku}</span>
                      </span>
                    </span>
                  </div>
                  <div>
                    <Label htmlFor={`sku-${u.id}`} className="text-xs">
                      {t("inventory:ninjamoSku")}
                    </Label>
                    <Input
                      id={`sku-${u.id}`}
                      value={sku}
                      onChange={(e) =>
                        setSkuDrafts((d) => ({ ...d, [u.id]: e.target.value.toUpperCase() }))
                      }
                      className={cn("mt-1 font-mono", bad && "border-destructive")}
                    />
                    {collides && (
                      <p className="mt-1 text-xs text-[hsl(var(--chart-3))]">
                        {t("inventory:skuExistsWillLink")}
                      </p>
                    )}
                    {duped && !collides && (
                      <p className="mt-1 text-xs text-[hsl(var(--chart-3))]">
                        {t("inventory:skuDuplicateInBatch")}
                      </p>
                    )}
                    {tooShort && (
                      <p className="mt-1 text-xs text-destructive">{t("inventory:skuTooShort")}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setReviewOpen(false)}>
              {t("inventory:cancel")}
            </Button>
            <Button
              type="button"
              disabled={bulkCreate.isPending || reviewSkus.some((s) => s.length < 2)}
              onClick={() =>
                bulkCreate.mutate({
                  ids: reviewRows.map((u) => u.id),
                  skus: Object.fromEntries(reviewRows.map((u) => [u.id, effectiveSku(u)])),
                })
              }
            >
              {bulkCreate.isPending
                ? t("inventory:saving")
                : t("inventory:bulkCreateSkus", { count: reviewRows.length })}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* "List on a channel" — prepares a draft listing for a channel this
          SKU isn't on yet. Creates the channel_listings row (and with it the
          per-channel price/allocation/content surface), so the SKU is staged
          and ready the moment real publishing lands. Nothing is pushed live. */}
      <Dialog
        open={listingProduct !== null}
        onOpenChange={(open) => !open && setListingProduct(null)}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {t("inventory:listOnChannelTitle", { sku: listingProduct?.sku })}
            </DialogTitle>
            <DialogDescription>{t("inventory:listOnChannelHint")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {CHANNEL_FILTERS.map((c) => (
              <Button
                key={c}
                type="button"
                variant="outline"
                className="w-full justify-start"
                disabled={listOnChannel.isPending}
                onClick={() =>
                  listingProduct &&
                  listOnChannel.mutate({ sku: listingProduct.sku, marketplace: c })
                }
              >
                <Store className="h-4 w-4" aria-hidden />
                {c}
              </Button>
            ))}
            {listError && <p className="text-sm text-destructive">{listError}</p>}
          </div>
        </DialogContent>
      </Dialog>

      {/* "Link listing" — from a matched SKU, search unmatched listings and
          link one. Isaac's ask (2026-08-29): matching should happen right
          from Inventory, not by navigating to Marketplaces and re-finding
          the same row. */}
      <Dialog
        open={linkingProduct !== null}
        onOpenChange={(open) => {
          if (!open) {
            setLinkingProduct(null);
            setLinkSearch("");
            setLinkChannel("all");
          }
        }}
      >
        <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {t("inventory:linkListingTitle", { sku: linkingProduct?.sku })}
            </DialogTitle>
            <DialogDescription>{t("inventory:linkListingHint")}</DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <Input
              autoFocus
              value={linkSearch}
              onChange={(e) => setLinkSearch(e.target.value)}
              placeholder={t("inventory:searchListings")}
            />
            <fieldset className="flex flex-wrap gap-1 border-0 p-0">
              <legend className="sr-only">{t("inventory:filterByChannel")}</legend>
              <Button
                type="button"
                size="sm"
                variant={linkChannel === "all" ? "default" : "ghost"}
                onClick={() => setLinkChannel("all")}
              >
                {t("inventory:allChannels")}
              </Button>
              {CHANNEL_FILTERS.map((c) => (
                <Button
                  key={c}
                  type="button"
                  size="sm"
                  variant={linkChannel === c ? "default" : "ghost"}
                  onClick={() => setLinkChannel(c)}
                >
                  {c}
                </Button>
              ))}
            </fieldset>

            <div className="space-y-1.5">
              {linkSearching && (
                <p className="text-sm text-muted-foreground">{t("common:loading")}</p>
              )}
              {!linkSearching && (linkResults ?? []).length === 0 && (
                <p className="text-sm text-muted-foreground">{t("inventory:noListingsFound")}</p>
              )}
              {(linkResults ?? []).map((r) => (
                <div
                  key={r.id}
                  className="flex items-center justify-between gap-2 rounded-md border p-2.5"
                >
                  <div className="min-w-0">
                    <span className="block truncate text-sm">{r.title || r.channelSku}</span>
                    <span className="flex flex-wrap items-center gap-1.5">
                      <Badge variant="outline">{r.marketplace}</Badge>
                      <span className="font-mono text-xs text-muted-foreground">
                        {r.channelSku}
                      </span>
                      {r.price && (
                        <span className="text-xs text-muted-foreground">
                          {money(r.price, "USD", locale)}
                        </span>
                      )}
                    </span>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    disabled={linkListingMutation.isPending}
                    onClick={() =>
                      linkingProduct &&
                      linkListingMutation.mutate({
                        unmatchedId: r.id,
                        productId: linkingProduct.id,
                      })
                    }
                  >
                    {t("inventory:link")}
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* "Match" (from an unmatched listing) / "Resolve" (from a cross-channel
          group) — search existing products or spin up a new one, without
          leaving Inventory. Mirrors Marketplaces.tsx's map/create-product
          flow exactly (same two endpoints), just anchored here instead. */}
      <Dialog
        open={resolvingListing !== null}
        onOpenChange={(open) => !open && setResolvingListing(null)}
      >
        <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{resolvingListing?.title || resolvingListing?.channelSku}</DialogTitle>
            <DialogDescription className="font-mono text-xs">
              {resolvingListing?.channelSku}
            </DialogDescription>
          </DialogHeader>

          <span className="inline-flex gap-1">
            <Button
              type="button"
              size="sm"
              variant={resolveMode === "link" ? "default" : "outline"}
              onClick={() => setResolveMode("link")}
            >
              {t("inventory:linkToExisting")}
            </Button>
            <Button
              type="button"
              size="sm"
              variant={resolveMode === "create" ? "default" : "outline"}
              onClick={() => setResolveMode("create")}
            >
              {t("inventory:createNewSku")}
            </Button>
          </span>

          {resolveMode === "link" ? (
            <div className="space-y-2">
              <Input
                autoFocus
                value={resolveSearch}
                onChange={(e) => setResolveSearch(e.target.value)}
                placeholder={t("inventory:searchProducts")}
              />
              <div className="space-y-1.5">
                {resolveSearchDebounced.trim().length > 0 &&
                  (resolveResults ?? []).length === 0 && (
                    <p className="text-sm text-muted-foreground">
                      {/* When the catalogue is genuinely empty, "no results"
                          reads as a broken search rather than "there is
                          nothing to link to yet" — say which it is. */}
                      {rows.length === 0
                        ? t("inventory:noProductsYet")
                        : t("inventory:noProductsFound")}
                    </p>
                  )}
                {(resolveResults ?? []).map((p) => (
                  <div
                    key={p.id}
                    className="flex items-center justify-between gap-2 rounded-md border p-2.5"
                  >
                    <div className="min-w-0">
                      <span className="block truncate text-sm">{p.title}</span>
                      <span className="font-mono text-xs text-muted-foreground">{p.sku}</span>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      disabled={resolveLinkMutation.isPending}
                      onClick={() =>
                        resolvingListing &&
                        resolveLinkMutation.mutate({
                          unmatchedId: resolvingListing.id,
                          productId: p.id,
                        })
                      }
                    >
                      {t("inventory:link")}
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <form onSubmit={handleResolveCreate} className="space-y-3">
              <div>
                <Label htmlFor="resolve-sku">
                  {t("inventory:ninjamoSku")}{" "}
                  <span className="text-xs font-normal text-muted-foreground">
                    {t("inventory:suggestedEditable")}
                  </span>
                </Label>
                <Input
                  id="resolve-sku"
                  value={resolveNewSku}
                  onChange={(e) => setResolveNewSku(e.target.value.toUpperCase())}
                  className="mt-1 font-mono"
                  required
                />
                {resolvingListing && resolveNewSku === resolvingListing.channelSku && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t("inventory:sameAsMarketplaceSku")}
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="resolve-title">{t("inventory:product")}</Label>
                <Input
                  id="resolve-title"
                  value={resolveNewTitle}
                  onChange={(e) => setResolveNewTitle(e.target.value)}
                  className="mt-1"
                  required
                />
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div>
                  <Label htmlFor="resolve-unit">{t("inventory:filterByUnit")}</Label>
                  <Select
                    value={resolveNewUnit}
                    onValueChange={(v) => setResolveNewUnit(v as Unit)}
                  >
                    <SelectTrigger id="resolve-unit" className="mt-1 w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {INVENTORY_UNITS.map((u) => (
                        <SelectItem key={u} value={u}>
                          {t(`common:units.${u}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="resolve-kind">{t("inventory:kind")}</Label>
                  <Select
                    value={resolveNewKind}
                    onValueChange={(v) => {
                      const kind = v as ProductKind;
                      setResolveNewKind(kind);
                      setResolveNewLicense(defaultLicenseKindForKind(kind));
                    }}
                  >
                    <SelectTrigger id="resolve-kind" className="mt-1 w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PRODUCT_KINDS.map((k) => (
                        <SelectItem key={k} value={k}>
                          {t(`inventory:productKind.${k}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="resolve-license">{t("inventory:licenseKind")}</Label>
                  <Select
                    value={resolveNewLicense}
                    onValueChange={(v) => setResolveNewLicense(v as LicenseKind)}
                  >
                    <SelectTrigger id="resolve-license" className="mt-1 w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {LICENSE_KINDS.map((l) => (
                        <SelectItem key={l} value={l}>
                          {t(`inventory:license.${l}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex justify-end">
                <Button type="submit" disabled={resolveCreateMutation.isPending}>
                  {resolveCreateMutation.isPending
                    ? t("inventory:saving")
                    : t("inventory:createProduct")}
                </Button>
              </div>
            </form>
          )}

          {resolveError && <p className="text-sm text-destructive">{resolveError}</p>}
        </DialogContent>
      </Dialog>

      {rows.length > 0 && (
        <Card ref={adjustRef} className="mt-6">
          <CardHeader className="p-4 pb-2">
            <CardTitle className="text-base">{t("inventory:adjustStock")}</CardTitle>
            <CardDescription>{t("inventory:adjustStockHint")}</CardDescription>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <form
              onSubmit={handleAdjust}
              className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3"
            >
              <div className="sm:col-span-2">
                <Label htmlFor="adjust-sku">{t("inventory:sku")}</Label>
                <Select value={adjustSku} onValueChange={setAdjustSku}>
                  <SelectTrigger id="adjust-sku" className="mt-1 w-full">
                    <SelectValue placeholder={t("inventory:selectSku")} />
                  </SelectTrigger>
                  <SelectContent>
                    {rows.map((r) => (
                      <SelectItem key={r.id} value={r.sku}>
                        {r.sku} — {r.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="adjust-kind">{t("inventory:movementKindLabel")}</Label>
                <Select value={adjustKind} onValueChange={(v) => setAdjustKind(v as MovementKind)}>
                  <SelectTrigger id="adjust-kind" className="mt-1 w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MOVEMENT_KINDS.map((k) => (
                      <SelectItem key={k} value={k}>
                        {t(`inventory:movementKind.${k}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <fieldset className="border-0 p-0">
                <legend className="mb-1 text-sm font-medium">{t("inventory:direction")}</legend>
                <span className="inline-flex gap-1">
                  <Button
                    type="button"
                    size="sm"
                    variant={adjustDirection === "add" ? "default" : "outline"}
                    onClick={() => setAdjustDirection("add")}
                  >
                    {t("inventory:directionAdd")}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={adjustDirection === "remove" ? "default" : "outline"}
                    onClick={() => setAdjustDirection("remove")}
                  >
                    {t("inventory:directionRemove")}
                  </Button>
                </span>
              </fieldset>
              <div>
                <Label htmlFor="adjust-qty">{t("inventory:quantity")}</Label>
                <Input
                  id="adjust-qty"
                  type="number"
                  min="1"
                  step="1"
                  value={adjustQty}
                  onChange={(e) => setAdjustQty(e.target.value)}
                  required
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="adjust-location">{t("inventory:location")}</Label>
                <Input
                  id="adjust-location"
                  value={adjustLocation}
                  onChange={(e) => setAdjustLocation(e.target.value)}
                  placeholder="main"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="adjust-reference">{t("inventory:reference")}</Label>
                <Input
                  id="adjust-reference"
                  value={adjustReference}
                  onChange={(e) => setAdjustReference(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="adjust-note">{t("inventory:note")}</Label>
                <Input
                  id="adjust-note"
                  value={adjustNote}
                  onChange={(e) => setAdjustNote(e.target.value)}
                  className="mt-1"
                />
              </div>

              <div className="flex items-end sm:col-span-2 lg:col-span-3">
                <Button type="submit" disabled={!adjustSku || !adjustQty || postMovement.isPending}>
                  {postMovement.isPending ? t("inventory:saving") : t("inventory:applyMovement")}
                </Button>
              </div>

              {adjustError && (
                <p className="text-sm text-destructive sm:col-span-2 lg:col-span-3">
                  {adjustError}
                </p>
              )}
            </form>
          </CardContent>
        </Card>
      )}

      {/* Bulk licence review -- sets licenceKind on every product the CURRENT
          filter matches (unit/search/channel/licence), not just this page.
          Confirms before writing since this can touch hundreds of rows. */}
      <Dialog open={licenseBulkOpen} onOpenChange={(open) => !open && setLicenseBulkOpen(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t("inventory:licenseBulkTitle")}</DialogTitle>
            <DialogDescription>
              {rows.length >= 200
                ? t("inventory:licenseBulkHintAtLeast", { count: rows.length })
                : t("inventory:licenseBulkHint", { count: rows.length })}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="license-bulk-target">{t("inventory:licenseBulkTarget")}</Label>
              <Select
                value={licenseBulkTarget}
                onValueChange={(v) => setLicenseBulkTarget(v as LicenseKind)}
              >
                <SelectTrigger id="license-bulk-target" className="mt-1 w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LICENSE_KINDS.filter((lk) => lk !== "unreviewed").map((lk) => (
                    <SelectItem key={lk} value={lk}>
                      {t(`inventory:license.${lk}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {licenseBulkError && <p className="text-sm text-destructive">{licenseBulkError}</p>}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setLicenseBulkOpen(false)}>
                {t("inventory:cancel")}
              </Button>
              <Button
                type="button"
                disabled={bulkSetLicense.isPending}
                onClick={() => bulkSetLicense.mutate()}
              >
                {bulkSetLicense.isPending
                  ? t("inventory:saving")
                  : t("inventory:licenseBulkApply", { count: rows.length })}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

const CHANNEL_STATUS_VARIANT: Record<
  ListingDetail["status"],
  "secondary" | "success" | "warning" | "outline"
> = {
  draft: "outline",
  pending: "secondary",
  active: "success",
  suppressed: "warning",
  ended: "outline",
};

/** The expanded per-SKU row: one line per channel with status/price/quantity/
 * allocation/run-rate, plus the >100%-allocation warning (informational only
 * — independent-per-channel was the confirmed model, see Inventory.tsx's
 * allocationSum comment). `detail`/`runRate` are the parent's already-fetched
 * queries, passed down rather than re-fetched here. */
function ChannelBreakdown({
  detail,
  runRate,
  profit,
  allocationSum,
  locale,
  t,
  onEdit,
  onUnlink,
  unlinking,
}: {
  detail: ProductDetail | undefined;
  runRate: RunRate | undefined;
  profit: ProfitDetail | undefined;
  allocationSum: number;
  locale: string;
  t: (key: string, opts?: Record<string, unknown>) => string;
  onEdit: (listing: ListingDetail) => void;
  onUnlink: (listingId: string) => void;
  unlinking: boolean;
}) {
  if (!detail) {
    return <p className="text-sm text-muted-foreground">{t("common:loading")}</p>;
  }
  const runRateByChannel = new Map(
    (runRate?.byChannel ?? []).map((c) => [`${c.marketplace}:${c.accountLabel}`, c]),
  );

  return (
    <div className="space-y-2">
      {allocationSum > 100 && (
        <p className="flex items-center gap-1.5 text-sm text-[hsl(var(--chart-3))]">
          <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
          {t("inventory:allocationOverWarning", { sum: Math.round(allocationSum) })}
        </p>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-muted-foreground">
              <th className="pb-1.5 pr-3 font-normal">{t("inventory:channel")}</th>
              <th className="pb-1.5 pr-3 font-normal">{t("inventory:marketplaceSku")}</th>
              <th className="pb-1.5 pr-3 font-normal">{t("inventory:marketplaceIdCol")}</th>
              <th className="pb-1.5 pr-3 font-normal">{t("inventory:statusLabel")}</th>
              <th className="pb-1.5 pr-3 text-right font-normal">{t("inventory:listPrice")}</th>
              <th className="pb-1.5 pr-3 text-right font-normal">{t("inventory:published")}</th>
              <th className="pb-1.5 pr-3 text-right font-normal">{t("inventory:intended")}</th>
              <th className="pb-1.5 pr-3 text-right font-normal">
                {t("inventory:allocationPercent")}
              </th>
              <th className="pb-1.5 pr-3 text-right font-normal">{t("inventory:runRate")}</th>
              <th className="pb-1.5 text-right font-normal">{t("inventory:actions")}</th>
            </tr>
          </thead>
          <tbody>
            {detail.listings.map((l) => {
              const rr = runRateByChannel.get(`${l.marketplace}:${l.accountLabel}`);
              return (
                <tr key={l.id} className="border-t">
                  <td className="py-1.5 pr-3">
                    {(() => {
                      // Links straight to the live listing on the marketplace
                      // (the ASIN page, the eBay item, etc.). Falls back to
                      // plain text when we have no marketplace id yet, rather
                      // than rendering a link that would 404.
                      const href = marketplaceListingUrl(l.marketplace, l.marketplaceId);
                      const label = (
                        <>
                          <span className="font-medium">{l.marketplace}</span>{" "}
                          <span className="text-xs text-muted-foreground">{l.accountLabel}</span>
                        </>
                      );
                      return href ? (
                        <a
                          href={href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 hover:underline"
                          title={t("inventory:openOnMarketplace", { channel: l.marketplace })}
                        >
                          {label}
                          <ExternalLink className="h-3 w-3 shrink-0" aria-hidden />
                        </a>
                      ) : (
                        label
                      );
                    })()}
                  </td>
                  <td className="py-1.5 pr-3 font-mono text-xs">
                    {l.channelSku ? (
                      <a
                        href={`/marketplaces?marketplace=${encodeURIComponent(l.marketplace)}&q=${encodeURIComponent(l.channelSku)}`}
                        className="hover:underline"
                        title={t("inventory:viewOnMarketplacesTab")}
                      >
                        {l.channelSku}
                      </a>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="py-1.5 pr-3 font-mono text-xs">{l.marketplaceId ?? "—"}</td>
                  <td className="py-1.5 pr-3">
                    <Badge variant={CHANNEL_STATUS_VARIANT[l.status]}>
                      {t(`inventory:listingStatus.${l.status}`, { defaultValue: l.status })}
                    </Badge>
                  </td>
                  <td className="py-1.5 pr-3 text-right tabular-nums">
                    {money(l.price, "USD", locale)}
                  </td>
                  <td className="py-1.5 pr-3 text-right tabular-nums text-muted-foreground">
                    {l.publishedQuantity != null ? integer(l.publishedQuantity, locale) : "—"}
                  </td>
                  <td className="py-1.5 pr-3 text-right tabular-nums font-medium">
                    {l.intendedQuantity != null ? integer(l.intendedQuantity, locale) : "—"}
                  </td>
                  <td className="py-1.5 pr-3 text-right tabular-nums text-muted-foreground">
                    {l.allocationPercent != null ? `${l.allocationPercent}%` : "—"}
                  </td>
                  <td className="py-1.5 pr-3 text-right tabular-nums text-muted-foreground">
                    {rr && rr.unitsPerDay > 0
                      ? t("inventory:unitsPerDay", { count: rr.unitsPerDay })
                      : "—"}
                  </td>
                  <td className="py-1.5 text-right">
                    <span className="inline-flex gap-1.5">
                      <Button type="button" size="sm" variant="outline" onClick={() => onEdit(l)}>
                        <Pencil className="h-3.5 w-3.5" aria-hidden />
                        {t("inventory:edit")}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={unlinking}
                        title={t("inventory:unlinkHint")}
                        onClick={() => onUnlink(l.id)}
                      >
                        <Unlink className="h-3.5 w-3.5" aria-hidden />
                        {t("inventory:unlink")}
                      </Button>
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {profit && (
        <div className="space-y-1.5 pt-1">
          <p className="text-xs font-medium text-muted-foreground">
            {t("inventory:marginBreakdown")}
          </p>
          {profit.unitCostUsed == null && (
            <p className="text-xs text-muted-foreground">{t("inventory:marginUnknownNote")}</p>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-muted-foreground">
                  <th className="pb-1.5 pr-3 font-normal">{t("inventory:channel")}</th>
                  <th className="pb-1.5 pr-3 text-right font-normal">{t("sales:revenue")}</th>
                  <th className="pb-1.5 pr-3 text-right font-normal">
                    {t("inventory:landedCost")}
                  </th>
                  <th className="pb-1.5 pr-3 text-right font-normal">
                    {t("inventory:channelFee")}
                  </th>
                  <th className="pb-1.5 pr-3 text-right font-normal">{t("inventory:adSpend")}</th>
                  <th
                    className="pb-1.5 pr-3 text-right font-normal"
                    title={t("inventory:shippingCostHint")}
                  >
                    {t("inventory:shippingCost")}
                  </th>
                  <th className="pb-1.5 text-right font-normal">{t("inventory:netMargin")}</th>
                </tr>
              </thead>
              <tbody>
                {profit.byChannel.map((c) => (
                  <tr key={`${c.marketplace}:${c.accountLabel}`} className="border-t">
                    <td className="py-1.5 pr-3">
                      <span className="font-medium">{c.marketplace}</span>{" "}
                      <span className="text-xs text-muted-foreground">{c.accountLabel}</span>
                    </td>
                    <td className="py-1.5 pr-3 text-right tabular-nums">
                      {money(c.revenueUsd, "USD", locale)}
                    </td>
                    <td className="py-1.5 pr-3 text-right tabular-nums text-muted-foreground">
                      {money(c.landedCostTotal, "USD", locale)}
                    </td>
                    <td className="py-1.5 pr-3 text-right tabular-nums text-muted-foreground">
                      {c.channelFee == null
                        ? "—"
                        : `${money(c.channelFee, "USD", locale)} (${c.feePercent}%)`}
                    </td>
                    <td className="py-1.5 pr-3 text-right tabular-nums text-muted-foreground">
                      {money(c.adSpendUsd, "USD", locale)}
                    </td>
                    <td
                      className="py-1.5 pr-3 text-right tabular-nums text-muted-foreground"
                      title={
                        c.unitsSoldMerchant > 0
                          ? `${c.unitsSoldMerchant} FBM unit${c.unitsSoldMerchant === 1 ? "" : "s"}`
                          : undefined
                      }
                    >
                      {c.unitsSoldMerchant === 0 ? "—" : money(c.shippingCost, "USD", locale)}
                    </td>
                    <td
                      className={cn(
                        "py-1.5 text-right font-medium tabular-nums",
                        c.netMargin != null && Number(c.netMargin) < 0 && "text-destructive",
                      )}
                    >
                      {money(c.netMargin, "USD", locale)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  Icon,
  warn,
}: {
  label: string;
  value: string;
  Icon: React.ComponentType<{ className?: string }>;
  warn?: boolean;
}) {
  return (
    <Card>
      <CardHeader className="p-4 pb-1">
        <CardTitle className="flex items-center gap-2 text-sm font-normal text-muted-foreground">
          <Icon className="h-4 w-4" />
          {label}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4 pt-0">
        <span
          className={cn(
            "block text-3xl font-semibold tabular-nums",
            warn && "text-[hsl(var(--chart-3))]",
          )}
        >
          {value}
        </span>
      </CardContent>
    </Card>
  );
}
